import React from 'react'
import * as IBAN from 'iban'
import { observer } from 'mobx-react'
import { action, observable } from 'mobx'
import { addPaymentInputRef, getPaymentInputRef } from './globalPaymentInputs'
import {
  Tab,
  Tabs,
  Form,
  Button,
  Modal,
  Row,
  Col,
  Tooltip,
  Spinner,
} from 'react-bootstrap'
import {
  CommonForm,
  tokenExIframe,
  PaymentMarketForm,
  toastState,
  overlayState,
  Pagination,
  ImageComponent,
} from 'src/views/components'
import {
  application,
  trackEnhancedCheckout,
  trackCustomCheckoutEvent,
  trackAddPaymentInfoEvent,
  capitalizeFirstLetter,
  getTotalPagesForPagination,
  isGPSEnabled,
  i18nTranslate,
  isExpressCheckout,
  getLiveEventStatus,
  scrollState,
  enableBillingAddressRequired,
  getDefaultPaymentMethod,
  enableNewFeature,
  getGPSConfig,
  getLocaleCodeFromUrl,
  isTwikeyPaymentName,
  isSepaMandateInProgress,
  isSepaPaymentType,
  isSepaPaymentRecord,
  isExternalGPSCreditCardPaymentName,
  BANK_TRANSFER_TYPE,
  GPS_CCARD_TYPE,
  hasNonExcludedSavedPayment,
} from 'src/utils'
import {
  renderFormInput,
  cardCVVSchema,
  handleEnterKeyPress,
} from 'src/views/components/PaymentListWithForm/PaymentAccordionInput'
import {
  getSepaIconURL,
  isCardProviderRestricted,
} from 'src/utils/paymentContainerHelper'
import isArray from 'lodash/isArray'
import { PaymentIcon } from 'src/views/components/PaymentIcon'
import { SepaMandateWarningBanner } from 'src/views/components/SepaMandateWarningBanner'
import {
  customerContainer,
  checkoutContainer,
  cartContainer,
  tokenExContainer,
  paymetricContainer,
  paypalContainer,
  applepayContainer,
  googlePayContainer,
} from 'src/models'
import {
  APPConfig,
  convertToBoolean,
  getLocalStorage,
  IS_BROWSER,
} from 'config/appConfig'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import { dateFormatDeps, getCardDetails, FormInputLabels } from 'src/deps'
import { isAmexCardProvider } from 'src/deps/CardDeps'
import {
  IoIosInformationCircle as InformationIcon,
  IoIosAdd,
} from 'react-icons/io'
import { TokenExCvvContainer, savedCardsContainer } from 'src/models/TokenEx'
import { PaymetricSavedCardCvvContainer } from 'src/models/Paymetric'
import { EditUpdatePaymentModal } from 'src/views/pages/MyAccount/Payments/EditUpdatePaymentModal'
import ExpressSectionHeader from '../ExpressSectionHeader'
import PaymentInstallment from '../PaymentListWithForm/PaymentInstallment'
import WirePayment from 'src/views/components/CheckoutAccordions/GlobalPaymentAccordion/WirePayment'
import './styles.scss'

const constructStyle = {
  formRowClassName: 'ml from-saved-list',
}

const cardBrandOrder = ['visa', 'mastercard', 'discover', 'amex']

const getCardBrand = displayName => {
  const normalizedName = (displayName || '').replace(/\s+/g, '').toLowerCase()

  if (normalizedName.includes('visa')) return 'visa'
  if (normalizedName.includes('mastercard')) return 'mastercard'
  if (normalizedName.includes('discover')) return 'discover'
  if (normalizedName.includes('americanexpress') || normalizedName === 'amex') {
    return 'amex'
  }

  return ''
}

const dedupePaymentIcons = paymentInfo => {
  if (!Array.isArray(paymentInfo)) return []

  const iconGroups = paymentInfo
    .map(payment => ({
      brand: getCardBrand(payment?.displayName),
      displayName: (payment?.displayName || '').trim().toLowerCase(),
      iconURL: (payment?.iconURL || '').trim().toLowerCase(),
      payment,
    }))
    .reduce((groups, icon) => {
      const matchingGroups = groups.filter(group =>
        group.some(
          groupedIcon =>
            (icon.brand && groupedIcon.brand === icon.brand) ||
            (icon.iconURL && groupedIcon.iconURL === icon.iconURL) ||
            (icon.displayName && groupedIcon.displayName === icon.displayName)
        )
      )
      const remainingGroups = groups.filter(
        group => !matchingGroups.includes(group)
      )
      return [...remainingGroups, [...matchingGroups.flat(), icon]]
    }, [])

  return iconGroups.map(group => {
    const preferredIcon = group.reduce((currentIcon, candidateIcon) => {
      const currentScore =
        Number(Boolean(currentIcon.iconURL)) * 2 +
        Number(Boolean(currentIcon.brand))
      const candidateScore =
        Number(Boolean(candidateIcon.iconURL)) * 2 +
        Number(Boolean(candidateIcon.brand))
      return candidateScore > currentScore ? candidateIcon : currentIcon
    })
    return preferredIcon.payment
  })
}

// Twikey rejects IBANs containing whitespace, so the value is normalized before it is stored or sent.
const normalizeIban = (ibanValue = '') =>
  (ibanValue || '').replace(/\s+/g, '').toUpperCase()

const getIbanValidationState = (ibanValue = '', urlCountryCode = '') => {
  const normalizedIban = normalizeIban(ibanValue)
  const ibanCountryCode = normalizedIban.slice(0, 2)
  const isEmptyIban = !normalizedIban
  const isValidIban = !isEmptyIban && IBAN.isValid(normalizedIban)

  const isCountryCodeMismatch =
    isValidIban &&
    !!normalizedIban &&
    !!urlCountryCode &&
    ibanCountryCode !== urlCountryCode

  return {
    isEmptyIban,
    isValidIban,
    isCountryCodeMismatch,
  }
}

class SepaIbanForm extends React.PureComponent {
  constructor(props) {
    super(props)
    this.urlCountryCode =
      (getLocaleCodeFromUrl() || '').split('_')?.[1]?.toUpperCase() || ''
    this.state = {
      ibanValue: props.initialIbanValue || '',
      ibanTouched: false,
    }
  }

  componentDidMount() {
    this.props?.onIbanEntryActiveChange?.(true)
    this.updateValidationStatus(this.state.ibanValue)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.initialIbanValue !== this.props.initialIbanValue) {
      const updatedIbanValue = this.props.initialIbanValue || ''
      this.setState({ ibanValue: updatedIbanValue })
      this.updateValidationStatus(updatedIbanValue)
    }
  }

  componentWillUnmount() {
    this.props?.onIbanEntryActiveChange?.(false)
  }

  getValidationState = (ibanValue = '') => {
    const validationState = getIbanValidationState(
      ibanValue,
      this.urlCountryCode
    )
    return {
      ...validationState,
      hasError:
        validationState.isEmptyIban ||
        !validationState.isValidIban ||
        validationState.isCountryCodeMismatch,
    }
  }

  updateValidationStatus = (ibanValue = '') => {
    this.props?.onIbanValidationChange?.(this.getValidationState(ibanValue))
  }

  handleChange = event => {
    const value = normalizeIban(event?.target?.value)
    this.setState({ ibanValue: value })
    this.props?.onIbanChange?.(value)
    this.updateValidationStatus(value)
  }

  handleBlur = () => {
    this.setState({ ibanTouched: true })
    this.updateValidationStatus(this.state.ibanValue)
    this.props?.onIbanBlur?.()
  }

  render() {
    const { ibanValue, ibanTouched } = this.state
    const { isEmptyIban, isValidIban, isCountryCodeMismatch } =
      this.getValidationState(ibanValue)

    return (
      <div className="pt-3 justify-content-between">
        <label htmlFor="iban-input" className="font-weight-bold">
          {i18nTranslate('payment.sepaIbanLabel', 'IBAN *')}
        </label>
        <input
          id="iban-input"
          type="text"
          className="form-control"
          placeholder={i18nTranslate(
            'payment.sepaIbanPlaceholder',
            'Enter your IBAN'
          )}
          value={ibanValue}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          autoComplete="off"
          maxLength={34}
          data-testid="qa-iban-input"
        />
        {ibanTouched && isEmptyIban && (
          <div className="iban-error-values" role="alert">
            {i18nTranslate(
              'validation.ibanRequired',
              'Please enter IBAN number'
            )}
          </div>
        )}
        {ibanTouched && !isEmptyIban && !isValidIban && (
          <div className="iban-error-values" role="alert">
            {i18nTranslate('validation.ibanInvalid', 'Invalid IBAN number')}
          </div>
        )}
        {isCountryCodeMismatch && (
          <div className="iban-error-values" role="alert">
            {i18nTranslate(
              'validation.ibanInvalidCountryCode',
              `IBAN country code does not match current market (${this.urlCountryCode})`
            )}
          </div>
        )}
      </div>
    )
  }
}

@observer
class GlobalPaymentsOptionsList extends React.Component {
  @observable selectedPayment = []
  @observable paymentList = customerContainer.paymentResponse?.payments || []
  @observable isToShowDeleteConfirmationModal = false
  @observable isDefaultCardId = ''
  @observable idToBeDeleted = ''
  @observable savedCardValidStatus = false
  @observable IdofCardToBeEdited = ''
  @observable isAddPaymentCallInProgress = false
  @observable isToShowEditUpdatePaymentModal = false
  @observable cardsDetails = ''
  @observable selectedPaymentTypeForAdd = ''
  showAddNewCardForm = false
  @observable showUseDifferentCardForm = false
  @observable showPaymentList = false
  @observable showModal = false
  @observable gpsPaymentApiLoaded = false
  @observable isToShowBillingAddressWarningModal = false
  @observable selectedPaymentForBillingAddData = false
  @observable gpsDisplayName = ''
  @observable mandateWarningCardId = ''
  tokenPathParam = ''
  previousCardID = ''
  isFromDidMount = false
  loadIframeInitially = false
  theDefaultCardId = ''
  isNotSelectedDefaultCard = false
  isFromDeleteCard = false
  currentTab = ''
  isCardExpired = true
  totalPagesLength = 0
  currentPage = 1
  ibanValueCache = checkoutContainer.iBanValue || ''
  customPaymentTypes = ['LOYALTY', 'STORECREDIT']

  /**
   * @description Returns the appropriate payment container based on Paymetric flag
   * @returns {Object} tokenExContainer or paymetricContainer
   */
  getPaymentContainer = () => {
    const enableDirectPaymetricAndAccertifyIntegration = convertToBoolean(
      APPConfig.getAppConfig().enableDirectPaymetricAndAccertifyIntegration
    )
    return enableDirectPaymetricAndAccertifyIntegration
      ? paymetricContainer
      : tokenExContainer
  }

  isPaymetricEnabled = () => {
    return convertToBoolean(
      APPConfig.getAppConfig().enableDirectPaymetricAndAccertifyIntegration
    )
  }

  getSavedCardCvvInputId = cardId => `savedCardCvvInput${cardId}`

  getSavedCardCvvErrorId = cardId => `savedCardCvvError${cardId}`

  cvvFormRef = React.createRef()

  /**
   * @description Pushes the CVV typed into the native input back into Formik.
   * The input is rendered via `RenderProps`, so CommonForm never binds it and
   * `values.cardCVV` would otherwise stay empty forever. Only the digit count
   * is validated, so a mask is stored and the real CVV never enters React state.
   * @param {string} cvv - Sanitised CVV digits.
   */
  syncCvvToFormik = (cvv = '') => {
    const maskedCvv = '0'.repeat(cvv?.length || 0)
    this.cvvFormRef?.current?.setFieldValue?.('cardCVV', maskedCvv, true)
  }

  /**
   * @description The Continue button is `type="button"` so a click cannot also
   * trigger Formik's submit. That removes the form's implicit submission, so
   * Enter is wired up here to keep the keyboard path working.
   * @param {Object} event - Keyboard event from the CVV input.
   */
  handleCvvKeyDown = event => {
    handleEnterKeyPress({
      event,
      isDisabled: checkoutContainer.isPaymentApiInProgress,
      onEnter: () => {
        Promise.resolve(this.handleSavedCreditCardContinue()).catch(error => {
          console.error('Saved credit card continue failed:', error)
        })
        this.triggerCvvFormAnnouncement()
      },
    })
  }

  isAmexCard = cardLabel => {
    return isAmexCardProvider(cardLabel?.provider)
  }

  validatePaymetricSavedCardCvv = ({ cardId = '', cardLabel = {} } = {}) => {
    const inputId = this.getSavedCardCvvInputId(cardId)
    const inputElement = document.getElementById(inputId)
    const errorElement = document.querySelector(
      `#cvvParent${cardId} .cardCvvtoken`
    )
    const rawValue = inputElement?.value || ''
    const cvv = rawValue.replace(/\D/g, '')
    const isValid = this.isAmexCard(cardLabel)
      ? /^\d{4}$/.test(cvv)
      : /^\d{3}$/.test(cvv)

    if (errorElement) {
      if (isValid) {
        errorElement.classList.remove('show')
      } else {
        errorElement.classList.add('show')
      }
    }
    this.updateCvvAriaState(cardId, isValid)
    this.syncCvvToFormik(cvv)

    this.savedCardValidStatus = isValid
    savedCardsContainer.tokenCvvValidData = { isValid }
    return { isValid, cvv }
  }

  /**
   * @description Keeps the CVV input's ARIA error state in sync with the
   * imperatively toggled error text so screen readers only announce a live error.
   * @param {string} cardId - Saved card id.
   * @param {boolean} isValid - Current CVV validity.
   */
  updateCvvAriaState = (cardId = '', isValid = false) => {
    const inputElement = document.getElementById(
      this.getSavedCardCvvInputId(cardId)
    )
    if (!inputElement) {
      return
    }
    inputElement.setAttribute('aria-invalid', String(!isValid))
    if (isValid) {
      inputElement.removeAttribute('aria-describedby')
    } else {
      inputElement.setAttribute(
        'aria-describedby',
        this.getSavedCardCvvErrorId(cardId)
      )
    }
  }

  onPaymetricSavedCardCvvChange = (cardId = '', cardLabel = {}, event) => {
    const inputElement = event?.target
    if (!inputElement) {
      return
    }
    const maxLength = this.isAmexCard(cardLabel) ? 4 : 3
    inputElement.value = (inputElement.value || '')
      .replace(/\D/g, '')
      .slice(0, maxLength)

    const errorElement = document.querySelector(
      `#cvvParent${cardId} .cardCvvtoken`
    )
    if (errorElement?.classList?.contains('show')) {
      this.validatePaymetricSavedCardCvv({ cardId, cardLabel })
    } else {
      this.syncCvvToFormik(inputElement.value)
    }
    this.resetSubmitOrderRetryState()
  }

  createSavedCardCvvContainer = () => {
    return this.isPaymetricEnabled()
      ? new PaymetricSavedCardCvvContainer()
      : new TokenExCvvContainer()
  }

  constructor(props) {
    super(props)

    if (this.props?.initiallySelectedPayment) {
      this.selectedPayment = this.props.initiallySelectedPayment
      // Keep the shared container in sync so submit-time flows (SEPA/GPS)
      // charge the selected card rather than defaulting to the first card.
      savedCardsContainer.selectedSavedPaymentId =
        this.props.initiallySelectedPayment?.id || ''
    }
    if (this.props?.isGpsPaymentsApiLoaded) {
      this.gpsPaymentApiLoaded = this.props?.isGpsPaymentsApiLoaded
    }
    const updatePaymentList =
      this.paymentList &&
      this.paymentList?.length &&
      this.paymentList?.filter(list => {
        const type = list?.type?.toLowerCase() || ''
        // const name = list?.name?.toLowerCase() || ''
        return type === 'creditcard' || type === 'gps'
      })
    this.paymentList = updatePaymentList
  }

  /**
   * @description Sets the Default Selected Tab and Default Selected Card
   * Loads CVV Field for Default Selected Card.
   */
  async componentDidMount() {
    this.currentTab = this.getDefaultPaymentTab()
    checkoutContainer.currentPaymentTypeTab = this.currentTab
    if (isExpressCheckout() && this.currentTab === 'savedcards') {
      checkoutContainer.selectedPaymentType = 'creditcard'
    }

    this.handleDefaultChecked()
    this.getPaymentContainer().tokenExValidData = false
    let selectedCardId = this.selectedPayment?.id || ''
    if (
      this.selectedPayment?.isDefault &&
      this.previousCardID !== selectedCardId
    ) {
      this.isFromDidMount = !this.loadIframeInitially
      this.cvvIframeTokenLoader()
    }
    if (isExpressCheckout()) {
      const { payments = [], pageableInfo = {} } =
        customerContainer.paymentResponse || {}
      const hasFilteredSavedPayments = hasNonExcludedSavedPayment(payments)

      if (
        (!customerContainer.isRegisterUser || !hasFilteredSavedPayments) &&
        !getLocalStorage('gpsRedirection')
      ) {
        this.handleGPSPaymentTypeSelect(
          APPConfig.getAppConfig()?.defaultPaymentMethod || 'CREDITCARD',
          this.getProcessorURL(),
          0,
          false
        )
      }
      if (
        !customerContainer.isRegisterUser ||
        (!hasFilteredSavedPayments && customerContainer.isRegisterUser)
      ) {
        this.showAddNewCardForm = true
        this.showUseDifferentCardForm = true
        this.showPaymentList =
          !!customerContainer?.paymentResponse.payment?.length
      }
      if (!cartContainer.cartResponse?.payments) {
        if (!hasFilteredSavedPayments) {
          this.showUseDifferentCardForm = true
          this.showAddNewCardForm = true
        } else {
          this.showPaymentList = true
        }
      }
    }
    checkoutContainer.getWirePaymentTranslationData()
  }

  /**
   * @description Loads CVV Field for Selected Card
   */
  componentDidUpdate(prevProps) {
    let selectedCardId = this.selectedPayment?.id || ''
    if (
      this.currentTab == 'savedcards' &&
      (this.previousCardID !== selectedCardId || this.isFromDidMount)
    ) {
      this.isFromDidMount = false
      this.cvvIframeTokenLoader()
    }
    if (
      isExpressCheckout() &&
      (!cartContainer.cartResponse?.payments ||
        prevProps.accountsTaxIdResponse !== this.props.accountsTaxIdResponse ||
        prevProps.isTaxFormDataSubmited !== this.props.isTaxFormDataSubmited)
    ) {
      if (
        !hasNonExcludedSavedPayment(
          customerContainer.paymentResponse?.payments
        )
      ) {
        this.showUseDifferentCardForm = true
        this.showAddNewCardForm = true
      } else {
        this.showPaymentList = true
      }
    }
  }

  /**
   * @description Clears selectedSavedPaymentId from shared container on component unmount
   * to prevent stale state from persisting across sessions and affecting future payment submissions.
   */
  componentWillUnmount() {
    savedCardsContainer.selectedSavedPaymentId = ''
    checkoutContainer.isSepaIbanEntryActive = false
  }

  /**
   * @description Gets the default payment tab to show on initial load.
   * If Credit card is added to Profile, and if GPS is enabled and,
   * credit cards are supported in GPS, returns 'savedcards'.
   * Else if, GPS is not enabled and if payments are added to profile, returns 'savedcards'.
   * Otherwise, returns 'newCard'.
   */
  getDefaultPaymentTab = () => {
    const isToShowSavedCardsTabByDefault = this.isToShowSavedCardsTab(
      this.paymentList
    )
    const defaultTab = isToShowSavedCardsTabByDefault ? 'savedcards' : 'newCard'
    return defaultTab
  }

  /**
   *  @description Handles the state of the submit button based on the payment type.
   *               This function is only executed if the checkout is in express mode.
   * @param {string} type - The type of payment selected.
   *                        Expected values are 'CREDITCARD', 'GOOGLEPAY', 'APPLEPAY, 'ALIPAY' ...
   *                        If the type is 'CREDITCARD', the submit order button will be enabled.
   *                        Otherwise, the submit order button will be disabled.
   */
  @action
  handleSubmitState(type) {
    if (!isExpressCheckout()) {
      return
    }
    const normalizedType = (type || '').toLowerCase()
    checkoutContainer.selectedPaymentType = normalizedType
    checkoutContainer.isToDisableSubmitOrderButton =
      normalizedType !== 'creditcard'
    if (checkoutContainer.selectedPaymentType === 'creditcard') {
      paypalContainer.paypalInitiate = false
      paypalContainer.paypalClientCreated = false
    }
    if (checkoutContainer.selectedPaymentType !== 'applepay') {
      applepayContainer.applepayInitiate = false
      applepayContainer.applepayClientCreated = false
    }
    if (checkoutContainer.selectedPaymentType !== 'googlepay') {
      googlePayContainer.isToInitiateGooglePayPayment = false
      googlePayContainer.googlePayClientCreated = false
    }
    if (checkoutContainer.selectedPaymentType !== 'klarna') {
      checkoutContainer.isToInitiateKlarnaPayment = false
    }
    if (checkoutContainer.selectedPaymentType !== 'alipay') {
      this.props?.closeAlipayInitiate()
    }
    if (checkoutContainer.selectedPaymentType !== 'paypal') {
      paypalContainer.paypalInitiate = false
      paypalContainer.paypalClientCreated = false
    }
  }
  /**
   * @description Checks if the saved cards tab should be shown by default.
   * Returns true if GPS is enabled, credit cards are supported in GPS,
   * and there are saved cards in the profile.
   * Or, returns true if GPS is disabled and there are saved cards in the profile.
   * Otherwise returns false.
   */
  isToShowSavedCardsTab = (paymentsList = []) => {
    const enableGlobalPaymentSystem = isGPSEnabled()
    const isCreditCardTypeSupportedInGPS =
      Object.keys(checkoutContainer.globalPaymentResponse)?.length > 0 &&
      Object.keys(checkoutContainer.globalPaymentResponse)?.some(
        paymentType => paymentType?.toLowerCase() === 'creditcard'
      )
    const hasFilteredPayments = hasNonExcludedSavedPayment(paymentsList)
    let isToShowSavedCardsTabByDefault = enableGlobalPaymentSystem
      ? isCreditCardTypeSupportedInGPS && hasFilteredPayments
      : hasFilteredPayments
    return isToShowSavedCardsTabByDefault
  }

  /**
   * @description Function to Load CVV FIled from TokenEx for Selected Credit Card.
   */
  cvvIframeTokenLoader = async () => {
    let selectedCardId = this.selectedPayment?.id || ''
    const isPlainCvvGPSCard = this.isPlainCvvSavedGPSCard(
      this.selectedPayment?.name
    )
    if (this.isPaymetricEnabled() || isPlainCvvGPSCard) {
      this.previousCardID = selectedCardId || ''
      this.savedCardValidStatus = false
      savedCardsContainer.tokenCvvValidData = { isValid: false }
      savedCardsContainer.tokenExCvvData = {}
      return
    }
    let tokenExUpdate = savedCardsContainer.savedCardsList[selectedCardId]
    this.previousCardID = selectedCardId || ''
    this.savedCardValidStatus = false
    tokenExUpdate = this.createSavedCardCvvContainer()
    tokenExUpdate.containerId = selectedCardId
    tokenExUpdate.errorValidationCallBack = (containerId, validData) => {
      const errorElement = document.querySelector(
        `#cvvParent${containerId} .cardCvvtoken`
      )
      if (validData.isValid == false) {
        errorElement?.classList?.add('show')
        this.savedCardValidStatus = false
        if (isExpressCheckout()) {
          overlayState.hideLoader()
        }
      } else {
        errorElement?.classList?.remove('show')
        this.savedCardValidStatus = true
      }
    }

    savedCardsContainer.savedCardsList[this.selectedPayment.id] = tokenExUpdate

    const tokenValues = customerContainer?.paymentResponse?.payments || []
    checkoutContainer.activeSubmitOrderButton = true
    this.tokenPathParam = tokenValues?.find(
      item => item?.id === selectedCardId
    )?.accountInfo?.token
    this.cardProvider = tokenValues?.find(
      item => item?.id === selectedCardId
    )?.accountInfo?.provider
    tokenExUpdate?.loadTokenExCvvIframe(
      `cvvTokenID${this.selectedPayment.id}`,
      this.tokenPathParam,
      this.cardProvider,
      this.selectedPayment
    )
  }

  /**
   * @description Function to Handle Page Selection.
   * Triggers GetPayment API with selected Page Number
   * And Sets Default Payment from the list and Loads CVV Field for default Card.
   */
  handlePagination = async (activePage = 1) => {
    const response = await customerContainer.getPayment(activePage)
    const transformedPaymentResponse =
      customerContainer.transformPaymentResponse(response, activePage) || {}
    this.paymentList = transformedPaymentResponse?.payments || []
    this.handleDefaultChecked()
    transformedPaymentResponse?.payments?.find(cards => {
      if (cards?.id === this.isDefaultCardId) {
        this.cvvIframeTokenLoader()
      }
    })
    this.currentPage = transformedPaymentResponse?.pageableInfo?.page || 1
  }

  resetSubmitOrderRetryState = () => {
    checkoutContainer.isToDisableSubmitOrderButton = false
    if (isExpressCheckout()) {
      checkoutContainer.activeSubmitOrderButton = true
    }
  }

  /**
   * @description Function to Handle Add Payment to Cart logic for Selected Saved Credit Card.
   */
  handleSavedCreditCardContinue = async () => {
    // Validate terms checkbox for live events before proceeding
    const enableLiveEvent = convertToBoolean(getLiveEventStatus())
    const termsCheckbox = document.getElementById('event-submit-terms-checkbox')
    const isTermsChecked = termsCheckbox ? termsCheckbox.checked : true

    if (enableLiveEvent && !isTermsChecked) {
      return false
    }
    // Defence in depth: never submit a SEPA card whose mandate is still pending.
    if (isSepaMandateInProgress(this.selectedPayment)) {
      return false
    }
    const { title = '' } = this.props
    const products = cartContainer?.cartResponse?.items || []

    const isBillingAddressRequired = enableBillingAddressRequired()

    if (
      this.selectedPayment?.billingAddress === undefined &&
      isBillingAddressRequired
    ) {
      this.resetSubmitOrderRetryState()
      toastState.setToastMessage(
        i18nTranslate(
          'billingAddressRequired',
          'Billing address not available. please add billing address in payment to proceed'
        )
      )
      return false
    }

    trackEnhancedCheckout({
      type: 'checkout',
      action: { step: '6', option: title },
      products: products,
    })
    checkoutContainer.isPaymentApiInProgress = true
    paypalContainer.paypalInitiate = false
    paypalContainer.paypalClientCreated = false
    googlePayContainer.isToInitiateGooglePayPayment = false
    googlePayContainer.googlePayClientCreated = false
    checkoutContainer.isToDisableSubmitOrderButton = false
    const currentCardId = this.selectedPayment?.id || ''
    const errorElement = document.querySelector(
      `#cvvParent${currentCardId} .cardCvvtoken`
    )
    const cardLabel = getCardDetails(this.selectedPayment?.accountInfo || {})
    let isCvvValid = this.savedCardValidStatus
    const isPlainCvvGPSCard = this.isPlainCvvSavedGPSCard(
      this.selectedPayment?.name
    )
    if (this.isPaymetricEnabled() || isPlainCvvGPSCard) {
      const { isValid, cvv } = this.validatePaymetricSavedCardCvv({
        cardId: currentCardId,
        cardLabel,
      })
      isCvvValid = isValid
      this.props?.changeCvvValidFlag(isCvvValid)
      if (isCvvValid) {
        const tokenExData = {
          cvvIncluded: false,
          cvv,
        }
        savedCardsContainer.tokenExCvvData = tokenExData
        savedCardsContainer.tokenCvvValidData = { isValid: true }
        this.props.handlePayment?.({
          cardCVV: cvv,
          ...this.selectedPayment,
          savedCardValidStatus: isCvvValid,
          isFromSavedAddress: true,
          tokenExData,
        })
        errorElement?.classList?.remove('show')
      } else {
        errorElement?.classList?.add('show')
        checkoutContainer.isPaymentApiInProgress = false
        this.resetSubmitOrderRetryState()
      }
      return
    }

    this.props?.changeCvvValidFlag(isCvvValid)
    if (isCvvValid) {
      if (isExpressCheckout()) {
        overlayState.showLoader()
      }
      checkoutContainer.isPaymentApiInProgress = false
      await savedCardsContainer.savedCardsList[currentCardId].getToken(
        tokenExData => {
          this.props.handlePayment?.({
            cardCVV: '',
            ...this.selectedPayment,
            savedCardValidStatus: isCvvValid,
            isFromSavedAddress: true,
            tokenExData,
          })
          errorElement?.classList?.remove('show')
        }
      )
    } else {
      errorElement?.classList?.add('show')
      checkoutContainer.isPaymentApiInProgress = false
      this.resetSubmitOrderRetryState()
    }
  }

  /**
   * @description Function to render CVV Field for Selected Saved Credit Card.
   * @param {Object} selectedCard
   * Handling Iframe Loads through this states if any card is deleted
   */
  isPlainCvvSavedGPSCard = paymentName => {
    return isExternalGPSCreditCardPaymentName(
      paymentName,
      checkoutContainer.globalPaymentResponse?.CREDITCARD || []
    )
  }

  // Any saved-card switch in express checkout must invalidate the payment that
  // was already added to the cart, otherwise submit order keeps the previously
  // added card's paymentRefId instead of the newly selected card.
  resetExpressSavedCardSelectionState = selectedCard => {
    const selectedCardId = selectedCard?.id || ''
    if (!isExpressCheckout() || this.selectedPayment?.id === selectedCardId) {
      return
    }

    checkoutContainer.expressPaymentAdded = false
    checkoutContainer.autoPaymentSuccess = false
    checkoutContainer.enableAutoSubmit = false
    checkoutContainer.activeSubmitOrderButton = true
    checkoutContainer.isToDisableSubmitOrderButton = false
    savedCardsContainer.tokenCvvValidData = { isValid: false }
    savedCardsContainer.tokenExCvvData = {}
    this.savedCardValidStatus = false
  }

  resolveSelectedPaymentType = selectedCard => {
    if (isTwikeyPaymentName(selectedCard?.name)) {
      return selectedCard?.name?.toLowerCase()
    }
    if (this.isPlainCvvSavedGPSCard(selectedCard?.name)) {
      return selectedCard?.name?.toLowerCase()
    }
    if ((selectedCard?.type || '').toUpperCase() === BANK_TRANSFER_TYPE) {
      return 'wire'
    }
    const cardType = selectedCard?.type?.toLowerCase() || ''
    // Saved credit cards stored as GPS records must stay on the credit-card
    // flow; returning 'gps' routes submit to handleUnpaidPayment, which creates
    // a bare `{ name: 'gps', type: 'GPS' }` payment with no card reference.
    return cardType === 'gps' ? 'creditcard' : cardType
  }

  updateDefaultCardAndCvvContainer = (selectedCard, allPayments) => {
    allPayments?.find(card => {
      if (card?.isDefault === true) {
        card.isDefault = false
      }
      if (card?.id === selectedCard?.id) {
        card.isDefault = true
        if (
          !this.isPaymetricEnabled() &&
          !this.isPlainCvvSavedGPSCard(selectedCard?.name) &&
          !savedCardsContainer.savedCardsList[selectedCard?.id]
        ) {
          savedCardsContainer.savedCardsList[selectedCard.id] =
            this.createSavedCardCvvContainer()
        }
      }
    })
  }

  /**
   * @description Shows the mandate-in-progress warning while the user
   * hovers or keyboard-focuses that specific read-only SEPA card.
   */
  handleSavedSepaCardHover = (id = '', isMandateInProgress = false) => {
    if (isMandateInProgress) {
      this.mandateWarningCardId = id
    }
  }

  handleSavedSepaCardHoverEnd = (id = '') => {
    if (this.mandateWarningCardId === id) {
      this.mandateWarningCardId = ''
    }
  }

  handleSavedCardOptionChange = (selectedCard = {}) => {
    this.resetExpressSavedCardSelectionState(selectedCard)
    const isBillingAddressRequired = enableBillingAddressRequired()
    if (
      selectedCard?.billingAddress === undefined &&
      isBillingAddressRequired
    ) {
      this.isToShowBillingAddressWarningModal = true
      this.selectedPaymentForBillingAddData = selectedCard
      return
    }

    if (this.isFromDeleteCard) {
      this.isFromDidMount = true
      this.isFromDeleteCard = false
    }

    this.updateDefaultCardAndCvvContainer(
      selectedCard,
      customerContainer.paymentResponse?.payments
    )
    this.selectedPayment = selectedCard
    savedCardsContainer.selectedSavedPaymentId = selectedCard?.id || ''
    if (isExpressCheckout()) {
      checkoutContainer.currentPaymentTypeTab = 'savedcards'
    }

    if (selectedCard?.type) {
      checkoutContainer.selectedPaymentType =
        this.resolveSelectedPaymentType(selectedCard)

      // Only a SEPA record carries an IBAN; other card types expose an
      // unrelated `accountInfo.token` that must not leak into the IBAN state.
      if (isSepaPaymentRecord(selectedCard)) {
        const savedSepaIban = normalizeIban(
          selectedCard?.accountInfo?.iban ||
            selectedCard?.accountInfo?.token ||
            selectedCard?.accountInfo?.IBAN ||
            selectedCard?.properties?.iban ||
            ''
        )
        this.ibanValueCache = savedSepaIban
        checkoutContainer.iBanValue = savedSepaIban
        // The saved record already holds a validated IBAN and mandate.
        checkoutContainer.iBanValidationStatus = {
          isEmptyIban: false,
          isValidIban: true,
          isCountryCodeMismatch: false,
          hasError: false,
        }
      } else {
        this.clearSepaIbanValue()
      }
    }
  }

  /**
   * @description Clears the cached IBAN and its validation status, without
   * touching the mount-driven `isSepaIbanEntryActive` flag.
   */
  clearSepaIbanValue = () => {
    this.ibanValueCache = ''
    checkoutContainer.iBanValue = ''
    checkoutContainer.iBanValidationStatus = {
      isEmptyIban: true,
      isValidIban: false,
      isCountryCodeMismatch: false,
      hasError: true,
    }
  }

  /**
   * @description Resets SEPA IBAN fields when switching away from SEPA payment method.
   */
  resetSepaIbanFields = () => {
    this.clearSepaIbanValue()
    checkoutContainer.isSepaIbanEntryActive = false
  }

  /**
   * @description Function to set the selected payment type when adding a new payment.
   * @param {String} paymentType
   */
  handleGPSPaymentTypeSelect = async (
    type = '',
    processorURL,
    key,
    loadCVV = false,
    isFromPaymentChange = false
  ) => {
    if (
      !isFromPaymentChange &&
      this.selectedPaymentTypeForAdd &&
      this.selectedPaymentTypeForAdd !== type &&
      cartContainer.cartResponse?.payments &&
      isExpressCheckout()
    ) {
      await this.deleteExistingPayment()
    }
    // Reset SEPA IBAN when switching away from SEPA
    if (
      isSepaPaymentType(this.selectedPaymentTypeForAdd) &&
      !isSepaPaymentType(type)
    ) {
      this.resetSepaIbanFields()
    }
    this.selectedPaymentTypeForAdd = type?.toLowerCase() || ''
    checkoutContainer.selectedPaymentType = this.selectedPaymentTypeForAdd
    if (isSepaPaymentType(this.selectedPaymentTypeForAdd)) {
      // Adding a new SEPA entry shares the 'sepa' type with saved records
      // named 'SEPA'; drop any earlier saved-card selection so submit treats
      // this as a new entry (IBAN validation) instead of a saved payment.
      savedCardsContainer.selectedSavedPaymentId = ''
    }

    if (
      isExpressCheckout() &&
      this.selectedPaymentTypeForAdd === 'creditcard' &&
      (loadCVV || this.showUseDifferentCardForm || this.showAddNewCardForm)
    ) {
      checkoutContainer.currentPaymentTypeTab = 'newCard'
    }

    const isInternalPayment =
      typeof processorURL === 'string'
        ? processorURL?.toLowerCase()?.includes('internal')
        : false
    checkoutContainer.isToInitiateGlobalPayment = !isInternalPayment
    if (this.selectedPaymentTypeForAdd.toLowerCase() === 'creditcard') {
      if (!isExpressCheckout() || loadCVV) {
        this.cvvIframeTokenLoader()
      }
      checkoutContainer.addPaymentToProfile = customerContainer.isRegisterUser
    }

    if (isExpressCheckout()) {
      this.handleSubmitState(type)
      if (isSepaPaymentType(this.selectedPaymentTypeForAdd)) {
        return
      }
      if (this.selectedPaymentTypeForAdd.toLowerCase() !== 'creditcard') {
        overlayState.showLoader()
        scrollState.smoothScrollToModalElement(`#qa-checkout-payment-accordion`)
        if (!isFromPaymentChange && getDefaultPaymentMethod() !== 'razorpay') {
          await this.handleAlternatePayment(type)
        } else {
          if (type === 'paypal') {
            await this.deleteExistingPayment()
            await this.handlePaypalInitiate()
            await this.setCommunicationPreference(type)
            await this.trackPaymentData()
            return
          } else if (type === 'alipay') {
            this.props?.handleAlipayInitiate()
          } else if (
            type === 'klarna' &&
            (!getGPSConfig('enableKlarnaGPSFlow') ||
              !checkoutContainer.isToInitiateGlobalPayment)
          ) {
            this.props?.handleKlarnaInitiate()
          } else if (type === 'applepay') {
            await this.deleteExistingPayment()
            // excludes storecredit and loyalty payment delete
            await this.handleAlternatePayment(type)
          } else if (type === 'googlepay') {
            await this.deleteExistingPayment()
            // excludes storecredit and loyalty payment delete
            await this.handleAlternatePayment(type)
          } else {
            // For GPS custom alternate methods (e.g. Mercado Pago, ChileWebPay),
            // selection must patch payment into cart immediately in express flow.
            await this.deleteExistingPayment()
            await this.handleAlternatePayment(type)
            return
          }
          await this.setCommunicationPreference(type)
          await this.trackPaymentData()
        }
      }
    }
  }

  deleteExistingPayment = async () => {
    const paymentList = cartContainer?.cartResponse?.payments || []
    if (paymentList?.length) {
      overlayState.showLoader()
      await Promise.all(
        paymentList?.map(async payment => {
          if (!this.customPaymentTypes.includes(payment?.type)) {
            await checkoutContainer.deletePaymentById(payment.id)
          }
        })
      )
      overlayState.hideLoader()
    }
    if (isExpressCheckout()) {
      if (
        !cartContainer.cartResponse?.payments ||
        cartContainer.cartResponse?.payments?.some(
          payment => !this.customPaymentTypes.includes(payment?.type)
        )
      ) {
        this.showAddNewCardForm = true
        this.showUseDifferentCardForm = true
        this.showPaymentList =
          !!customerContainer?.paymentResponse.payment?.length
      }
    }
  }
  setCommunicationPreference = async (type = '') => {
    const communicationResponse =
      await checkoutContainer.setCommunicationPreference(
        cartContainer?.cartResponse?.deliveryDetails?.address || {}
      )

    if (!checkoutContainer.isSuccessResponse(communicationResponse)) {
      checkoutContainer.isPaymentApiInProgress = false
      toastState.setToastMessage(
        communicationResponse.message || communicationResponse?.errorMessage
      )
      this.isAddPaymentCallInProgress = false
      if (isExpressCheckout() && type.toLowerCase() === 'creditcard') {
        overlayState.hideLoader()
      } else if (isExpressCheckout() && type.toLowerCase() === 'wire') {
        overlayState.hideLoader()
      }
      return
    }
  }
  /**
   * @description Function to Handle Add Payment to Cart logic for Added New Paymeents other than CreditCard.
   */
  handleAlternatePayment = async (type = '') => {
    this.isAddPaymentCallInProgress = true
    try {
      await this.setCommunicationPreference(type)
      const bagValue = Math.abs(
        cartContainer?.cartResponse?.paymentValue?.totalAmountUnpaid || ''
      )
      const response = await checkoutContainer.handleUnpaidPayment(
        type,
        bagValue,
        this.props?.gpsPaymentList
      )
      this.gpsDisplayName = response?.getDisplayNameFromGps
      if (checkoutContainer.isSuccessResponse(response)) {
        paypalContainer.paypalInitiate = false
        paypalContainer.paypalClientCreated = false
        googlePayContainer.isToInitiateGooglePayPayment = false
        googlePayContainer.googlePayClientCreated = false
        if (type === 'paypal') {
          const paypaltoken = await checkoutContainer.getPayPalToken()
          this.props.handlePaypalInitiate(paypaltoken?.token)
        }
        if (type === 'alipay') {
          this.props?.handleAlipayInitiate()
        }
        if (
          type === 'klarna' &&
          (!getGPSConfig('enableKlarnaGPSFlow') ||
            !checkoutContainer.isToInitiateGlobalPayment)
        ) {
          this.props?.handleKlarnaInitiate()
        }
        if (type === 'applepay') {
          await this.handleApplePayInitiate()
        }
        if (type === 'googlepay') {
          await this.handleGooglePayInitiate()
        }
        await this.trackPaymentData()
      } else if (isExpressCheckout()) {
        overlayState.hideLoader()
      }
    } finally {
      this.isAddPaymentCallInProgress = false
    }
  }

  handleApplePayInitiate = async () => {
    const applePayToken =
      await checkoutContainer.getBrainTreePaymentToken('APPLEPAY')
    if (checkoutContainer.isSuccessResponse(applePayToken)) {
      this.props?.handleApplePayInitiate?.(applePayToken?.token, false)
    }
  }

  handleGooglePayInitiate = async () => {
    const googlePayToken =
      await checkoutContainer.getBrainTreePaymentToken('GOOGLEPAY')
    if (checkoutContainer.isSuccessResponse(googlePayToken)) {
      this.props?.handleGooglePayInitiate?.(googlePayToken?.token, false)
    }
  }

  handlePaypalInitiate = async () => {
    paypalContainer.paypalInitiate = false
    paypalContainer.paypalClientCreated = false
    const paypalToken = await checkoutContainer.getPayPalToken()
    if (checkoutContainer.isSuccessResponse(paypalToken)) {
      paypalContainer.paypalInitiate = true

      if (
        paypalContainer.paypalInitiate &&
        typeof braintree !== 'undefined' &&
        braintree?.client
      ) {
        if (
          paypalContainer.paypalClientCreated &&
          paypalContainer?.paypalCheckoutInstance
        ) {
          paypalContainer?.createPaypalButton(
            paypalContainer?.paypalCheckoutInstance
          )
        } else {
          braintree.client?.create(
            {
              authorization: paypalToken?.token,
            },
            function (clientErr, clientInstance) {
              paypalContainer.createPayPalCheckoutComponent(
                clientErr,
                clientInstance
              )
            }
          )
          paypalContainer.paypalClientCreated = true
        }
      }
    }
    this.props.handlePaypalInitiate(paypalToken?.token)
  }

  trackPaymentData = async () => {
    await cartContainer.viewCart()
    this.props?.onContinue()
    checkoutContainer.isToDisableSubmitOrderButton = false
    if (getDefaultPaymentMethod() !== 'razorpay') {
      const isPaymentInfoEventEnabled = enableNewFeature(
        'enablePaymentInfoGTMEvent'
      )
      if (isPaymentInfoEventEnabled) {
        trackAddPaymentInfoEvent({
          cartData: cartContainer?.cartResponse,
        })
      } else {
        trackCustomCheckoutEvent({
          eventType: 'payment',
          cartResponse: cartContainer?.cartResponse,
        })
      }
    }
    if (isExpressCheckout()) {
      this.showUseDifferentCardForm = true
      this.showAddNewCardForm = true
      this.showPaymentList = true
      overlayState.hideLoader()
    }
  }

  /**
   * @description Function to reset Values to defaultValue, upon Cancelling Payment.
   */
  cancelPaymentFlag = () => {
    tokenExIframe.isValidCard = true
    tokenExIframe.isCvvValidCard = true
    this.selectedPaymentTypeForAdd = ''
    checkoutContainer.isToDisableSubmitOrderButton = true
    checkoutContainer.isToInitiateGlobalPayment = false
  }

  /**
   * @description Handles Update/Edit button Click for a payment card.
   * Enables Edit/Update Payment Modal
   *
   * @param {Object} e - The click event object
   * @param {Object} cards - The card object to update
   */
  handleUpdateButtonClick = (e, cards = {}) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    this.isToShowEditUpdatePaymentModal = true
    this.cardsDetails = cards
    this.IdofCardToBeEdited = cards?.id
  }

  /**
   * @description Closes the Edit/Update Payment modal.
   */
  handleUpdateModalClose = () => {
    this.isToShowEditUpdatePaymentModal = false
  }

  /**
   * @description Handles Delete button Click for a payment card.
   * Enables Delete Payment Confirmation Modal
   *
   * @param {Object} e - The click event object
   * @param {Object} cards - The card object to update
   */
  handleDeleteButtonClick = (e, card = {}) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    this.isToShowDeleteConfirmationModal = true
    this.idToBeDeleted = card?.id
    this.cardsDetails = card
  }

  /**
   * @description Closes the Delete Payment Confirmation Modal.
   */
  handleDeleteConfirmationModalClose = () => {
    this.isToShowDeleteConfirmationModal = false
  }

  /**
   * @description Deletes the selected payment for the customer by ID.
   * Calls the customerContainer deletePayment API.
   * Closes the delete confirmation modal.
   * Handles resetting the selected payment if default was deleted.
   * Sets flags if default card was deleted to reload the iframe.
   */
  handleDeletePayment = (selectedCardId = '') => {
    customerContainer.deletePayment({
      id: selectedCardId,
      page: this.currentPage || '',
    })
    this.isToShowDeleteConfirmationModal = false
    this.cancelPaymentFlag()
    /**
     * @note
     * If non-default card is selected and is deleted, setting selectedPayment to default card, if default card is deleted then passing a state as true to load iframe on didupdate
     */
    if (this.isNotSelectedDefaultCard && ids != this.theDefaultCardId) {
      this.selectedPayment.id = this.theDefaultCardId
    } else if (this.isNotSelectedDefaultCard && ids === this.theDefaultCardId) {
      this.isFromDeleteCard = true
    } else if (!this.isNotSelectedDefaultCard) {
      this.previousCardID = ''
      this.isFromDeleteCard = true
    }
  }

  /**
   * @description Finds the default payment method and sets it as selected payment.
   */
  handleDefaultChecked = e => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    customerContainer.paymentResponse?.payments?.find(cards => {
      if (cards.isDefault === true && !isSepaMandateInProgress(cards)) {
        this.isDefaultCardId = cards.id
        this.selectedPayment = cards
        savedCardsContainer.selectedSavedPaymentId = cards?.id || ''
        /** Keep selectedPaymentType (and saved SEPA IBAN state) aligned with
         * configured GPS credit-card / SEPA saved payments so SubmitOrder can
         * resolve the pre-selected default card without the user re-clicking
         * an already-selected card. Scoped to express checkout to match the
         * plain-CVV GPS card branch and the reported express-checkout bug. */
        if (isExpressCheckout() && this.isPlainCvvSavedGPSCard(cards?.name)) {
          checkoutContainer.selectedPaymentType = cards?.name?.toLowerCase()
        } else if (isExpressCheckout() && isTwikeyPaymentName(cards?.name)) {
          checkoutContainer.selectedPaymentType = cards?.name?.toLowerCase()
          const savedSepaIban = normalizeIban(
            cards?.accountInfo?.iban ||
              cards?.accountInfo?.token ||
              cards?.accountInfo?.IBAN ||
              cards?.properties?.iban ||
              ''
          )
          const urlCountryCode =
            (getLocaleCodeFromUrl() || '').split('_')?.[1]?.toUpperCase() || ''
          const ibanValidationState = getIbanValidationState(
            savedSepaIban,
            urlCountryCode
          )
          this.ibanValueCache = savedSepaIban
          checkoutContainer.iBanValue = savedSepaIban
          checkoutContainer.iBanValidationStatus = {
            ...ibanValidationState,
            hasError:
              ibanValidationState.isEmptyIban ||
              !ibanValidationState.isValidIban ||
              ibanValidationState.isCountryCodeMismatch,
          }
        }
      }
    })
  }

  /**
   * @description Triggers Update Payment API to set Selected Card as Default.
   */
  handleDefaultChange = (e, selectedCardId = '', selectedCardName = '') => {
    const postData = { isDefault: true, name: selectedCardName }
    customerContainer.updateDefaultPayment({
      postData,
      id: selectedCardId,
    })
    this.isDefaultCardId = selectedCardId
  }

  /**
   * Sets the current tab that is selected.
   * @param {string} selectedTab - The tab id that is currently selected.
   */
  handleSelectedPaymentTab = (selectedTab = '') => {
    // Reset SEPA IBAN when switching away from SEPA
    if (
      isSepaPaymentType(this.selectedPaymentTypeForAdd) &&
      !isSepaPaymentType(selectedTab)
    ) {
      this.resetSepaIbanFields()
    }
    this.selectedPaymentTypeForAdd = ''
    this.currentTab = selectedTab
    checkoutContainer.currentPaymentTypeTab = selectedTab
    if (selectedTab.toLowerCase() === 'savedcards' && isExpressCheckout()) {
      this.selectedPaymentTypeForAdd = 'creditcard'
      checkoutContainer.selectedPaymentType = this.selectedPaymentTypeForAdd
    }
  }

  /**
   * @description `handleSavedCreditCardContinue` is invoked directly (not via
   * Formik's `onSubmit`), so Formik's `submitCount` otherwise never leaves 0 and
   * `CommonForm`'s "Form has one error" live region never announces. Calling
   * `submitForm()` alongside the direct invocation pumps `submitCount`/`errors`
   * for the announcement without changing what actually submits the payment -
   * `onSubmit` on the CVV form is a no-op specifically so this can't double-fire.
   */
  triggerCvvFormAnnouncement = () => {
    this.cvvFormRef?.current?.submitForm?.()
  }

  /**
   * @description Renders a continue button for saved credit card payments.
   * The continue button is disabled while the payment API call is in progress.
   * It triggers the handleSavedCreditCardContinue method on click.
   * @returns {JSX.Element} - The rendered continue button.
   */
  renderContinueButtonForSavedPayments = () => {
    return (
      <Form.Row className="justify-content-end">
        <Button
          className="px-5 py-2 saved-card-continue-button"
          data-testid="qa-pay-continue"
          id="payment-continue-button"
          type="button"
          disabled={checkoutContainer.isPaymentApiInProgress}
          onClick={event => {
            // CX121-3102 - to prevent multiple clicks
            // https://stackoverflow.com/a/42664936
            if (!event.detail || event.detail == 1) {
              this.handleSavedCreditCardContinue()
              this.triggerCvvFormAnnouncement()
            }
          }}>
          {checkoutContainer.isPaymentApiInProgress ? (
            <span className="px-4">
              <Spinner
                animation="border"
                role="status"
                size="md"
                variant="primary"
              />
            </span>
          ) : (
            i18nTranslate('checkout.continue', 'Continue')
          )}
        </Button>
      </Form.Row>
    )
  }

  /**
   * @description Renders confirm delete text for deleting a saved payment method.
   */
  renderDeletePaymentConfirmationText = () => {
    const cardNumber = this.cardsDetails?.accountInfo?.cardNumber || ''
    const cardProvider = this.cardsDetails?.accountInfo?.provider || ''
    const isTwikeyPayment = isTwikeyPaymentName(this.cardsDetails?.name)
    const isWirePayment =
      (this.cardsDetails?.type || '').toUpperCase() === BANK_TRANSFER_TYPE
    const sepaToken =
      this.cardsDetails?.accountInfo?.token ||
      this.cardsDetails?.accountInfo?.iban ||
      this.cardsDetails?.accountInfo?.IBAN ||
      this.cardsDetails?.properties?.iban ||
      ''
    const sepaEndingDigits = sepaToken ? sepaToken.slice(-4) : ''
    const wirePaymentName =
      this.cardsDetails?.name || i18nTranslate('checkout.wirePayment', 'Wire')

    let deletePaymentDetail = (
      <>
        <span>{capitalizeFirstLetter(cardProvider)} &nbsp;</span>
        <span>****{cardNumber}&nbsp;</span>
      </>
    )
    if (isTwikeyPayment) {
      deletePaymentDetail = (
        <>
          <span>
            {i18nTranslate(
              'payment.sepaDirectDebitEndingIn',
              'Sepa Direct Debit ending in '
            )}
          </span>
          <span>{sepaEndingDigits}&nbsp;</span>
        </>
      )
    } else if (isWirePayment) {
      deletePaymentDetail = <span>{wirePaymentName}</span>
    }

    return (
      <div>
        <div className="confirm-delete-modal-wrapper">
          <span>
            {i18nTranslate('payment.deleteMessageStart', 'Delete')}&nbsp;
          </span>
          {deletePaymentDetail}
          <span>
            {i18nTranslate(
              'payment.deleteMessageEnd',
              'from your saved payments?'
            )}
          </span>
        </div>
        <div className="d-flex justify-content-between delete-button-wrapper">
          <Button
            className="btn btn-outline-primary cancel-delete-button"
            data-testid="qa-delete-cancel-button"
            onKeyPress={this.handleDeleteConfirmationModalClose}
            onClick={this.handleDeleteConfirmationModalClose}>
            {i18nTranslate('payment.cancelDelete', 'Cancel')}
          </Button>
          <Button
            className="btn btn-primary confirm-delete-button"
            data-testid="qa-delete-confirm-button"
            onKeyPress={() => {
              this.handleDeletePayment(this.idToBeDeleted)
            }}
            onClick={() => {
              this.handleDeletePayment(this.idToBeDeleted)
            }}>
            {i18nTranslate('payment.confirmDelete', 'Confirm')}
          </Button>
        </div>
      </div>
    )
  }

  /**
   * @description Renders Delete Button, Edit/Update Button and Default Card Checkbox.
   *
   * @param {*} isSelected on which card user taking action
   * @param {*} cards contains all card related details
   * @param {*} deleteLabel delete text
   * @returns {*} edit/update and delete button in checkout page
   */
  renderDeleteCardBlock = (
    isSelected = false,
    cards = {},
    deleteLabel = ''
  ) => {
    if (!isSelected && cards?.id === this.isDefaultCardId) {
      this.isNotSelectedDefaultCard = true
      this.theDefaultCardId = cards?.id || ''
    } else if (isSelected && cards?.id === this.isDefaultCardId) {
      this.isNotSelectedDefaultCard = false
    }
  }

  /**
   * @description Renders Info Icon for CVV Field.
   * @returns {JSX.Element} cvv info icon
   */
  renderCVVInfoIcon = () => {
    return (
      <OverlayTrigger
        trigger={'click'}
        placement="top"
        rootClose={true}
        overlay={
          <Tooltip
            id={`tooltip`}
            className="saved-cvv-tooltip"
            data-testid="qa-cvv-info-tooltip">
            {i18nTranslate(
              'payment.cvvInfo',
              'To proceed with your transaction, enter your Card Verification Value found at the back of your card.'
            )}
          </Tooltip>
        }>
        <Button
          className="saved-cvv-tooltip-info-button"
          data-testid="qa-cvv-info-button">
          <InformationIcon
            size="1.5rem"
            id="cvv-info-black-icon"
            className="infotool"
            aria-label="information tool"
          />
        </Button>
      </OverlayTrigger>
    )
  }

  /**
   * @description Renders CVV Field for Saved Credit Card.
   * @returns {JSX.Element} Cvv Field
   */
  /**
   * @description True when the CVV renders as a native input rather than the
   * cross-origin TokenEx iframe, i.e. when Formik can actually hold its value.
   * @returns {boolean}
   */
  isNativeCvvInputRendered = () => {
    return (
      this.isPaymetricEnabled() ||
      this.isPlainCvvSavedGPSCard(this.selectedPayment?.name)
    )
  }

  renderCvvNumber = ([id, cardLabel]) => {
    if (this.isNativeCvvInputRendered()) {
      const maxLength = this.isAmexCard(cardLabel) ? 4 : 3
      const cvvText = i18nTranslate('securityCode', 'CVV')
      return (
        <div className="d-flex align-items-center saved-card-cvv-wrapper paymetric-saved-cvv-wrapper">
          <div className="tokenCvv-block md-2 mr-1" id={`cvvParent${id}`}>
            <input
              className="cvvIframeHeight form-control paymetric-saved-cvv-input"
              data-testid="qa-pay-code"
              id={this.getSavedCardCvvInputId(id)}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="new-password"
              maxLength={maxLength}
              onChange={event =>
                this.onPaymetricSavedCardCvvChange(id, cardLabel, event)
              }
              onBlur={() =>
                this.validatePaymetricSavedCardCvv({ cardId: id, cardLabel })
              }
              onKeyDown={this.handleCvvKeyDown}
              placeholder={cvvText}
              aria-label={cvvText}
            />
            <div
              className="cardCvvtoken"
              id={this.getSavedCardCvvErrorId(id)}
              role="alert">
              {i18nTranslate(
                'validation.cardCvvErrorText',
                'Please enter valid security code'
              )}
            </div>
          </div>
          {this.renderCVVInfoIcon(cardLabel)}
        </div>
      )
    }

    return (
      <div className="d-flex align-items-center saved-card-cvv-wrapper">
        <div className="tokenCvv-block md-2 mr-1" id={`cvvParent${id}`}>
          <div
            className="cvvIframeHeight"
            value=""
            id={`cvvTokenID${id}`}></div>
          <div className="cardCvvtoken" role="alert">
            {i18nTranslate(
              'validation.cardCvvErrorText',
              'Please enter valid security code'
            )}
          </div>
        </div>
        {this.renderCVVInfoIcon(cardLabel)}
      </div>
    )
  }

  /**
   * @description Renders Card details for Saved Credit Card.
   * @param {Object} cardLabel - Card details including provider, number, expiry
   * @param {Object} name - Customer name details
   * @param {Object} billingAddress - Optional billing address, will be retrieved from card if not provided
   * @returns {JSX.Element} Card Details
   */
  renderCardDetail = (cardLabel = '', name = '', billingAddress = null) => {
    const iconType = cardLabel?.provider?.toLowerCase() || ''
    const currentDateTimeStamp = Date.now()
    const { month, year } =
      dateFormatDeps.convertTimeStampToDate(currentDateTimeStamp)
    this.isCardExpired =
      year < cardLabel.expiryYear
        ? true
        : year == cardLabel?.expiryYear
          ? month <= cardLabel?.expiryMonth
            ? true
            : false
          : false

    const endingText = i18nTranslate('payment.cardEndingTxt', '-****')
    const expiryText = i18nTranslate('payment.cardExpiresText', 'Expires')
    const expiredText = i18nTranslate(
      'payment.cardExpiredText',
      'Card is Expired'
    )
    const isBillingAddressRequired = enableBillingAddressRequired()
    return (
      <>
        {!isExpressCheckout() && (
          <div className="mx-2 pb-2">{`${name?.firstName?.toUpperCase()} ${
            name?.firstName?.toUpperCase() !== name?.lastName?.toUpperCase()
              ? ` ${name?.lastName?.toUpperCase()}`
              : ''
          }`}</div>
        )}
        <div className="d-flex mr-2 ml-0">
          <PaymentIcon type={iconType} />
          <div className="credit-card-info-wrapper">
            <h5 className="card-info ml-1">
              {capitalizeFirstLetter(cardLabel?.provider)} {endingText}{' '}
              {cardLabel?.cardNumber}
            </h5>
            {(!isExpressCheckout() ||
              (isExpressCheckout() && this.isCardExpired)) && (
              <span
                className={`${
                  this.isCardExpired
                    ? 'paragraph-s ml-1 d-flex'
                    : 'paragraph-s text-danger ml-1 d-flex'
                } justify-content-around`}>
                {this.isCardExpired ? expiryText : expiredText}{' '}
                {`${cardLabel?.expiryMonth}/${cardLabel?.expiryYear?.substr(
                  2
                )}`}
              </span>
            )}
          </div>
        </div>
        {billingAddress && isBillingAddressRequired && isExpressCheckout() && (
          <span className="d-flex flex-row express-address-payment mt-2 flex-wrap">
            <span className="fname-normal-shiping">
              {`${name?.firstName?.toUpperCase()}${
                name?.firstName?.toUpperCase() !== name?.lastName?.toUpperCase()
                  ? ` ${name?.lastName?.toUpperCase()},`
                  : ','
              }`}
            </span>
            <span className="fname-normal-shiping" data-testid="qa-street">
              {billingAddress?.addressLine1 || ''}
            </span>
            <span className="fname-normal-shiping" data-testid="qa-street">
              {billingAddress?.addressLine2
                ? `${billingAddress?.addressLine2}, `
                : ''}
              {billingAddress?.city ? `${billingAddress?.city}, ` : ''}
              {billingAddress?.state ? `${billingAddress?.state}, ` : ''}
              {billingAddress?.zip && (
                <span className="fname-normal-shiping" data-testid="qa-zip">
                  {billingAddress?.zip},
                </span>
              )}
            </span>
            <span className="fname-normal-shiping" data-testid="qa-country">
              {billingAddress?.country || ''}
            </span>
          </span>
        )}
      </>
    )
  }

  /**
   * @description Renders SEPA payment details for Saved SEPA payment.
   * @param {String} ibanToken - Masked IBAN token
   * @param {Object} name - Customer name details
   * @param {Object} billingAddress - Optional billing address
   * @returns {JSX.Element} SEPA Payment Details
   */
  renderSepaPaymentDetail = (ibanToken = '', name = '') => {
    const maskedIban = ibanToken ? `${ibanToken.slice(-4)}` : ''
    // Reuse the same iconURL the "add new payment" SEPA option renders with, if available.
    const sepaIconURL = getSepaIconURL()
    return (
      <div className="text-left">
        <h5 className="card-info d-flex align-items-center">
          {sepaIconURL ? (
            <ImageComponent
              testId="qa-sepa-saved-icon"
              src={sepaIconURL}
              name="SEPA"
              width="40px"
              height="25px"
              title="SEPA"
              aria-label="SEPA-icon"
              className="payment-icons"
              customParentClassName="mr-1 mb-1"
              overrideParentClass={true}
            />
          ) : (
            <PaymentIcon type="sepa" />
          )}
          &nbsp;
          {i18nTranslate(
            'payment.sepaDirectDebitEndingIn',
            'Sepa Direct Debit ending in'
          )}
          &nbsp;
          {maskedIban}
        </h5>
      </div>
    )
  }

  /**
   * @description Renders payment details for a saved Wire/Bank Transfer payment.
   * @param {String} name - Saved payment display name
   * @returns {JSX.Element} Wire Payment Details
   */
  renderWirePaymentDetail = (name = '') => {
    return (
      <div className="text-left">
        <h5 className="card-info">
          {name || i18nTranslate('checkout.wirePayment', 'Wire')}
        </h5>
      </div>
    )
  }

  /**
   * @description Renders Saved Credit Card Options and its details.
   * @returns {JSX.Element} Card Options
   */
  renderSavedPaymentsList = () => {
    let paymentResponseArray = [...customerContainer.paymentResponse?.payments]
    paymentResponseArray.forEach((elem, idx) => {
      if (elem?.id === this.isDefaultCardId) {
        paymentResponseArray?.splice(idx, 1)
        paymentResponseArray?.unshift(elem)
      }
    })
    return paymentResponseArray.map(cards => {
      const {
        accountInfo = {},
        isDefault = false,
        type = '',
        id = '',
        billingAddress = null,
        properties = {},
        name = '',
      } = cards || {}
      const accountName = {
        firstName: accountInfo?.name || '',
        lastName: accountInfo?.lastName || '',
      }
      const isSelected = isDefault
      if (isSelected) {
        this.loadIframeInitially = true
      }

      const deleteLabel =
        type === 'CREDITCARD' || name === 'CREDITCARD'
          ? i18nTranslate('cart.deleteCard', 'Delete Card')
          : i18nTranslate('cart.delete', 'Delete')
      const cardLabel = getCardDetails(accountInfo)
      // Hides saved AMEX/DINERS/DISCOVER cards once their feature flag is
      // disabled, or Discover in Canada (both checked by isCardProviderRestricted).
      // Deliberately excludes shouldRestrictCardBasedOnGPS so GPS-market
      // behavior for other card types is unaffected.
      const isRestrictedSavedCard = isCardProviderRestricted(
        cardLabel?.provider
      )
      const isTwikeyPayment = isTwikeyPaymentName(name)
      const isMandateInProgress = isSepaMandateInProgress(cards)
      const isWirePayment = (type || '').toUpperCase() === BANK_TRANSFER_TYPE
      let savedPaymentDetail = this.renderCardDetail(
        cardLabel,
        accountName,
        billingAddress
      )
      if (isTwikeyPayment) {
        savedPaymentDetail = this.renderSepaPaymentDetail(
          accountInfo?.token,
          accountName,
          billingAddress
        )
      } else if (isWirePayment) {
        savedPaymentDetail = this.renderWirePaymentDetail(name)
      }
      // GPS-issued saved cards may not match isPlainCvvSavedGPSCard (e.g. the
      // configured processor list hasn't loaded yet); properties.paymentOptionType
      // is a secondary, more reliable signal for the same "this GPS card is a plain
      // credit card" case. Plain (non-GPS) CREDITCARD saves always show the CVV field.
      const showCvvField =
        type === 'CREDITCARD' ||
        name === 'CREDITCARD' ||
        (type === 'GPS' &&
          (this.isPlainCvvSavedGPSCard(name) ||
            properties?.paymentOptionType?.toLowerCase() === GPS_CCARD_TYPE))
      if (
        ((type === 'CREDITCARD' || name === 'CREDITCARD') &&
          !isRestrictedSavedCard) ||
        isTwikeyPayment ||
        this.isPlainCvvSavedGPSCard(name)
      ) {
        return (
          <div
            className={`flex-sm-row  mb-4 expresss-payment-card ${
              checkoutContainer.isPaymentApiInProgress
                ? 'disable-payment-section'
                : ''
            }
            ${
              isExpressCheckout() && isSelected
                ? 'expresss-payment-card-selected'
                : ''
            } `}
            key={'container' + id}
            data-testid="qa-saved-card">
            <div
              className="d-sm-grid justify-content-between"
              id="saved-card-wrapper"
              data-testid="qa-saved-card-row">
              <div
                className="saved-card-payment-row"
                style={{
                  width: '100%',
                  objectFit: 'contain',
                  display: 'flex',
                }}>
                <div
                  id="card-details-wrapper"
                  key={'inner' + id}
                  role="radio"
                  aria-checked={isSelected && !isMandateInProgress}
                  aria-describedby={
                    isMandateInProgress ? `sepa-warning-${id}` : undefined
                  }
                  tabIndex={0}
                  onMouseEnter={() =>
                    this.handleSavedSepaCardHover(id, isMandateInProgress)
                  }
                  onMouseLeave={() => this.handleSavedSepaCardHoverEnd(id)}
                  onFocus={() =>
                    this.handleSavedSepaCardHover(id, isMandateInProgress)
                  }
                  onBlur={() => this.handleSavedSepaCardHoverEnd(id)}
                  onClick={() =>
                    !isMandateInProgress &&
                    this.handleSavedCardOptionChange(cards)
                  }
                  onKeyPress={() =>
                    !isMandateInProgress &&
                    this.handleSavedCardOptionChange(cards)
                  }>
                  <Form.Check
                    id={`cards-${id}`}
                    name="paymentListRadios"
                    className="saved-payment-info-wrapper"
                    key={'radio' + id}
                    label={savedPaymentDetail}
                    type="radio"
                    checked={isSelected && !isMandateInProgress}
                    aria-checked={isSelected && !isMandateInProgress}
                    tabIndex={0}
                    data-testid={
                      isMandateInProgress
                        ? 'qa-facet-mandate-in-progress'
                        : isSelected
                          ? 'qa-facet-selected'
                          : 'qa-facet'
                    }
                    onClick={() =>
                      !isMandateInProgress &&
                      this.handleSavedCardOptionChange(cards)
                    }
                    onKeyPress={() =>
                      !isMandateInProgress &&
                      this.handleSavedCardOptionChange(cards)
                    }
                    disabled={
                      checkoutContainer.isPaymentApiInProgress ||
                      isMandateInProgress
                    }
                  />
                </div>
                {isMandateInProgress && this.mandateWarningCardId === id && (
                  <SepaMandateWarningBanner id={id} />
                )}
                {!application.isMobile &&
                  !isMandateInProgress &&
                  this.renderDeleteCardBlock(isSelected, cards, deleteLabel)}
                {isSelected && showCvvField && (
                  <div
                    id="gps-checkout-payment-cvv"
                    className="d-flex align-items-center">
                    <CommonForm
                      reference={this.cvvFormRef}
                      schema={cardCVVSchema(cardLabel, {
                        isCvvInputBound: this.isNativeCvvInputRendered(),
                      })}
                      formTypeJson={renderFormInput({
                        renderCvvNumber: this.renderCvvNumber.bind(this, [
                          cards.id,
                          cardLabel,
                        ]),
                      })}
                      SubmitButtonProps={
                        this.renderContinueButtonForSavedPayments
                      }
                      hasInlineSubmitButton={false}
                      classNameValue={constructStyle}
                      // handleSavedCreditCardContinue is invoked directly from
                      // the Continue button/Enter key above; this stays a
                      // no-op so triggerCvvFormAnnouncement's submitForm()
                      // call can't also invoke it and double-submit.
                      onSubmit={() => {}}
                    />
                  </div>
                )}
              </div>

              <div id="card-handlers-wrapper">
                <div
                  style={{
                    width: '100%',
                    objectFit: 'contain',
                  }}
                  role="checkbox"
                  tabIndex={isSelected ? 0 : -1}
                  aria-checked={cards?.id === this.isDefaultCardId}
                  onKeyPress={e => {
                    this.handleDefaultChange(e, cards?.id, cards?.name)
                  }}>
                  {isSelected && (
                    <Form.Check
                      type="checkbox"
                      label={i18nTranslate(
                        'checkoutpayment_setasdefault',
                        'Set as Default Card'
                      )}
                      id="set-default-checkout"
                      key={'checkbox' + cards?.id}
                      checked={cards?.id === this.isDefaultCardId}
                      onChange={e => {
                        this.handleDefaultChange(e, cards?.id, cards?.name)
                      }}
                      disabled={cards?.id === this.isDefaultCardId}
                      tabIndex={isSelected ? 0 : -1}
                    />
                  )}
                </div>
                {!isSelected && cards?.id === this.isDefaultCardId && (
                  <div>
                    <p className="default-payment-title" tabIndex={0}>
                      {i18nTranslate('payment.defaultTitle', 'Default Payment')}
                    </p>
                  </div>
                )}
              </div>
              {!isTwikeyPayment && !isWirePayment && (
                <div className="edit-delete-button-wrapper">
                  <span>
                    <Button
                      variant="link"
                      disabled={checkoutContainer.isPaymentApiInProgress}
                      className="font-weight-bold edit-update-button"
                      aria-label="Edit card link"
                      onClick={e => this.handleUpdateButtonClick(e, cards)}
                      data-testid={
                        this.isCardExpired
                          ? 'qa-edit-payment'
                          : 'qa-update-payment'
                      }>
                      {this.isCardExpired
                        ? i18nTranslate('edit', 'Edit')
                        : i18nTranslate('catalog.Update', 'Update', {
                            nameSpace: 'ssr-resource',
                          })}
                    </Button>
                  </span>
                  <span>
                    <Button
                      variant="link"
                      disabled={checkoutContainer.isPaymentApiInProgress}
                      className="font-weight-bold delete-button"
                      aria-label="delete card link"
                      onClick={e => this.handleDeleteButtonClick(e, cards)}
                      data-testid="qa-delete-payment">
                      {deleteLabel}
                    </Button>
                  </span>
                </div>
              )}
              {isTwikeyPayment && !isMandateInProgress && (
                <div className="edit-delete-button-wrapper">
                  <span>
                    <Button
                      variant="link"
                      disabled={checkoutContainer.isPaymentApiInProgress}
                      className="font-weight-bold delete-button"
                      aria-label="delete card link"
                      onClick={e => this.handleDeleteButtonClick(e, cards)}
                      data-testid="qa-delete-payment">
                      {deleteLabel}
                    </Button>
                  </span>
                </div>
              )}
              {isWirePayment && (
                <div className="edit-delete-button-wrapper">
                  <span>
                    <Button
                      variant="link"
                      disabled={checkoutContainer.isPaymentApiInProgress}
                      className="font-weight-bold delete-button"
                      aria-label="delete card link"
                      onClick={e => this.handleDeleteButtonClick(e, cards)}
                      data-testid="qa-delete-payment">
                      {deleteLabel}
                    </Button>
                  </span>
                </div>
              )}
            </div>
            {application.isMobile &&
              !isMandateInProgress &&
              this.renderDeleteCardBlock(isSelected, cards, deleteLabel)}
          </div>
        )
      }
      return <></>
    })
  }

  /**
   * @description Renders Saved Credit Card Options and its details.
   * @returns {JSX.Element} Card Options
   */
  renderNewCreditCardPaymentForm = () => {
    const { handlePayment, isFromCheckout = false } = this.props
    return (
      <>
        <div className="d-flex pt-3 justify-content-between px-3">
          <p className="font-weight-bold">
            {i18nTranslate('payment.CardDetailHeading', 'Credit Card Details')}
          </p>
          <div className="d-flex secureIcon">
            <svg
              width="16"
              height="20"
              viewBox="0 0 16 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3 5.83334C3 3.07192 5.23858 0.833344 8 0.833344C10.7614 0.833344 13 3.07192 13 5.83334V8.33334H13.8333C14.7538 8.33334 15.5 9.07953 15.5 10V17.5C15.5 18.4205 14.7538 19.1667 13.8333 19.1667H2.16667C1.24619 19.1667 0.5 18.4205 0.5 17.5V10C0.5 9.07953 1.24619 8.33334 2.16667 8.33334H3V5.83334ZM4.66667 8.33334H11.3333V5.83334C11.3333 3.99239 9.84095 2.50001 8 2.50001C6.15905 2.50001 4.66667 3.99239 4.66667 5.83334V8.33334ZM2.16667 10V17.5H13.8333V10H2.16667Z"
                fill="#5F5F5F"
              />
            </svg>
            <span className="secureIconText">
              {i18nTranslate('payment.secure', 'SECURE')}
            </span>
          </div>
        </div>
        <PaymentMarketForm
          onSubmitForm={handlePayment}
          isFromCheckout={isFromCheckout}
          cancelPaymentFlag={this.cancelPaymentFlag}
          billingAddressType="checkout"
        />
      </>
    )
  }

  @action
  handleIbanChange = (value = '') => {
    const normalizedValue = normalizeIban(value)
    this.ibanValueCache = normalizedValue
    checkoutContainer.iBanValue = normalizedValue
  }

  @action
  handleIbanValidationChange = (validationState = {}) => {
    checkoutContainer.iBanValidationStatus = validationState
  }

  @action
  handleIbanEntryActiveChange = (isActive = false) => {
    checkoutContainer.isSepaIbanEntryActive = !!isActive
  }

  /**
   * @description Renders Continue and Cancel Button for Add New Alternate Payments.
   * @returns {JSX.Element} Continue and Cancel Button
   */
  renderContinueButtonForOtherPayments = (type = '') => {
    return (
      <div className="d-block">
        <div className="mt-4 mb-2">
          <Row className="d-flex w-100 mb-3 mt-4" noGutters>
            <Col className="pr-1" md={6} sm={6} xs={6}>
              <Button
                type="submit"
                data-testid="qa-account-save"
                className="add-payment-continue-button w-100"
                id="wire-continue-payment-btn"
                disabled={this.isAddPaymentCallInProgress}
                onClick={() => this.handleAlternatePayment(type)}>
                {i18nTranslate('checkout.continue', 'Continue')}
              </Button>
            </Col>
            <Col className="pl-1" md={6} sm={6} xs={6}>
              <Button
                variant="outline-primary"
                type="submit"
                form="payment-form"
                className="add-payment-cancel-button w-100"
                data-testid="qa-add-new-pay-cancel"
                onClick={this.cancelPaymentFlag}>
                {i18nTranslate('checkout.cancel', 'Cancel')}
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    )
  }

  /**
   * @description Renders Klarna's terms of service and privacy policy.
   */
  renderKlarnaTermsAndServiceLink = () => {
    if (IS_BROWSER && this.selectedPaymentTypeForAdd === 'klarna') {
      const links = new FormInputLabels()
      return (
        <div
          className="klarna-terms-message"
          data-testid="qa-klarna-terms-message">
          <span className="pr-1">
            {i18nTranslate(
              'checkout.klarnaMessage1',
              'By continuing, I accept the'
            )}
          </span>
          <span
            className="pr-1 klarna-link"
            data-testid="qa-klarna-service-link">
            <a
              href={links?.retailCustomerLinks?.klarnaShoppingServiceLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={event => event.stopPropagation()}>
              {i18nTranslate(
                'checkout.klarnaServiceLink',
                'Klarna Shopping Service'
              )}
            </a>
          </span>
          <span className="pr-1">
            {i18nTranslate(
              'checkout.klarnaMessage2',
              'terms and confirm that I have read the'
            )}
          </span>
          <span data-testid="qa-klarna-policy-link" className="klarna-link">
            <a
              href={links?.retailCustomerLinks?.klarnaPrivacyPolicyLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={event => event.stopPropagation()}>
              {i18nTranslate('checkout.klarnaPrivacyLink', 'Privacy Policy.')}
            </a>
          </span>
        </div>
      )
    }
  }

  renderWirePaymentInfo = () => {
    return (
      <Modal show={this.showModal} onHide={() => (this.showModal = false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {i18nTranslate('payment.wireInfo', 'Wire Info')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <WirePayment />
        </Modal.Body>
      </Modal>
    )
  }

  handleShow = () => {
    this.showModal = true
  }
  /**
   * @description Renders a description for the given payment type.
   */
  renderDescriptionForPayment = (paymentType = '') => {
    if (paymentType === 'klarna') {
      return this.renderKlarnaTermsAndServiceLink()
    }
    if (isExpressCheckout() && paymentType === 'wire') {
      return (
        <OverlayTrigger
          trigger="hover"
          placement="top"
          rootClose={true}
          overlay={
            <Tooltip id="wire-info-tooltip">
              {i18nTranslate('payment.wireInfoTooltip', 'Click for wire info')}
            </Tooltip>
          }>
          <Button
            className="wire-payment-modal"
            data-testid="wire-payment-info-modal"
            aria-label={i18nTranslate(
              'payment.wireInfoAriaLabel',
              'More information about Wire transfers'
            )}
            onClick={this.handleShow}>
            <InformationIcon
              size="1.5rem"
              id="wire-info-black-icon"
              className="infotool"
              aria-hidden="true"
            />
          </Button>
        </OverlayTrigger>
      )
    }

    return <></>
  }

  /**
   * @description Renders Payment Icon for the add new payment options.
   */
  renderPaymentIcons = paymentDetails => {
    const {
      paymentInfo = {},
      paymentName = '',
      iconType,
    } = paymentDetails || {}
    const dedupedPaymentInfo = dedupePaymentIcons(paymentInfo).sort(
      (firstPayment, secondPayment) => {
        const firstRank = cardBrandOrder.indexOf(
          getCardBrand(firstPayment?.displayName)
        )
        const secondRank = cardBrandOrder.indexOf(
          getCardBrand(secondPayment?.displayName)
        )
        const normalizedFirstRank = firstRank === -1 ? Infinity : firstRank
        const normalizedSecondRank = secondRank === -1 ? Infinity : secondRank
        return normalizedFirstRank - normalizedSecondRank
      }
    )
    const isPaymentIconURLExists = dedupedPaymentInfo.some(payment =>
      Boolean(payment?.iconURL?.trim?.())
    )
    if (isPaymentIconURLExists) {
      return (
        <Row noGutters className="icon-wrapper">
          {dedupedPaymentInfo?.map((payment, index) => {
            const { displayName = '' } = payment
            const iconURL = (payment?.iconURL || '').trim()
            if (iconURL) {
              return (
                <ImageComponent
                  testId={`qa-${displayName}-icon`}
                  src={iconURL}
                  name={displayName}
                  key={`${displayName}-${index}`}
                  width="40px"
                  height="25px"
                  title={displayName}
                  aria-label={`${displayName}-icon`}
                  className="payment-icons"
                  customParentClassName="mr-1 mb-1"
                  overrideParentClass={true}
                />
              )
            }
            const localIconType = getCardBrand(displayName) || iconType
            return localIconType ? (
              <PaymentIcon
                key={`${displayName}-${index}`}
                type={localIconType}
              />
            ) : null
          })}
        </Row>
      )
    }

    const localCardBrands = [
      ...new Set(
        dedupedPaymentInfo
          .map(payment => getCardBrand(payment?.displayName))
          .filter(Boolean)
      ),
    ]
    if (localCardBrands.length === 1) {
      return <PaymentIcon type={localCardBrands[0]} />
    }
    if (localCardBrands.length > 1) {
      return (
        <Row noGutters className="icon-wrapper">
          {localCardBrands.map(brand => (
            <PaymentIcon key={brand} type={brand} />
          ))}
        </Row>
      )
    }
    if (iconType) return <PaymentIcon type={iconType} />

    return <p className="text-capitalize ml-2 mb-0">{paymentName}</p>
  }
  getStoreCreditsPaidAmount = () => {
    const storeCreditPayment = cartContainer?.cartResponse?.payments?.find(
      item => item?.type == 'STORECREDIT'
    )
    return storeCreditPayment?.amount || 0
  }

  /**
   * @description Renders Payment Option Radio Button for the add new payments list.
   */
  renderPaymentOptionRadioButton = (paymentDetails = {}) => {
    const {
      type = '',
      displayName = '',
      iconType = '',
      dataTestId = 'qa-payment-radio-option',
      paymentId = '',
      isToShowPaymentOption = false,
      paymentInfo = {},
      key = 0,
    } = paymentDetails || {}
    const paymentType = type?.toLowerCase() || ''
    const processorURL = paymentDetails?.paymentInfo?.[0]?.processorURL || ''
    const paymentDisplayName =
      paymentType === 'creditcard'
        ? i18nTranslate('checkout.creditCardText', 'Credit Card')
        : i18nTranslate(`checkout.${displayName.toLowerCase()}`, displayName)

    const removeStoreCreditForCustomPayments =
      APPConfig?.getAppConfig()?.removeStoreCreditForCustomPayments || []

    const enableWirePayment = convertToBoolean(
      APPConfig?.getAppConfig()?.enableWirePaymentForGuestUser
    )
    const enableStoreCreditWithChilePay = convertToBoolean(
      APPConfig?.getAppConfig()?.enableStoreCreditWithChilePay
    )
    const enableStoreCreditWithWirePay = convertToBoolean(
      APPConfig?.getAppConfig()?.enableStoreCreditWithWirePay
    )
    const storeCreditPaidAmount = this.getStoreCreditsPaidAmount()

    addPaymentInputRef({ paymentId })

    if (
      paymentType === 'wire' &&
      !customerContainer.isRegisterUser &&
      !enableWirePayment
    ) {
      return <></>
    }

    if (
      (!enableStoreCreditWithChilePay &&
        storeCreditPaidAmount > 0 &&
        (paymentType === 'chilewebpay' || paymentType === 'ChileWebpay')) ||
      (!enableStoreCreditWithWirePay &&
        storeCreditPaidAmount > 0 &&
        paymentType === 'wire') ||
      (storeCreditPaidAmount > 0 &&
        removeStoreCreditForCustomPayments.includes(paymentType?.toLowerCase()))
    ) {
      return <></>
    }

    // Hide Apple Pay if device doesn't support it (e.g., Windows)
    if (
      paymentType === 'applepay' &&
      !applepayContainer.checkIfDeviceSupportsApplePay()
    ) {
      return <></>
    }

    return (
      isToShowPaymentOption && (
        <>
          <input
            ref={getPaymentInputRef({ paymentId })}
            type="radio"
            name="paymentTypeRadios"
            id={paymentId}
            onChange={() =>
              this.handleGPSPaymentTypeSelect(
                paymentType,
                processorURL,
                key,
                false,
                true
              )
            }
            key={'radio' + paymentType}
            checked={this.selectedPaymentTypeForAdd === paymentType}
            className="payment-options w-100"
            data-testid={dataTestId}
            aria-label={`Selected ${paymentDisplayName}`}
            tabIndex={0}
          />
          <label htmlFor={paymentId} className="payment-label">
            <div className="add-payment-block-wrapper">
              <p
                className={`${
                  isSepaPaymentType(paymentType) ? '' : 'text-capitalize'
                } payment-type-title`}>
                {paymentDisplayName}
              </p>
              {this.renderPaymentIcons({
                paymentInfo,
                iconType,
                paymentName: displayName,
              })}
              {this.renderDescriptionForPayment(paymentType)}
            </div>
          </label>
        </>
      )
    )
  }

  /**
   * @description Renders Add New Payments List.
   * If GPS is Enabled, then Payment Options from GPS API is listed based on payment type
   * Else, Error Message is shown.
   */
  renderAddNewPaymentOptions = () => {
    const enableGlobalPaymentSystem = isGPSEnabled()
    const isSuccessResponse = checkoutContainer.isSuccessResponse(
      checkoutContainer.globalPaymentOptionsListResponse
    )

    if (!checkoutContainer.isGlobalPaymentCallInProgress) {
      if (
        enableGlobalPaymentSystem &&
        isSuccessResponse &&
        Object.keys(checkoutContainer.globalPaymentResponse)?.length > 0
      ) {
        return Object.keys(checkoutContainer.globalPaymentResponse)?.map(
          (paymentType, key) => {
            const paymentDetails =
              checkoutContainer.globalPaymentResponse[paymentType] || []
            if (isArray(paymentDetails) && paymentDetails?.length > 0) {
              let paymentName = ''
              if (paymentType !== 'CREDITCARD') {
                paymentName = paymentType || ''
              } else {
                paymentName = paymentType?.toLowerCase() || ''
              }
              // The SEPA response key is a content-managed label
              // ('SEPA Direct Debit'); its option id carries the stable type.
              const optionId = paymentDetails[0]?.id || ''
              const optionType = isSepaPaymentType(optionId)
                ? optionId
                : paymentName
              if (paymentName) {
                return this.renderPaymentOptionRadioButton({
                  type: optionType,
                  displayName: paymentName,
                  iconType: paymentName,
                  isToShowPaymentOption: true, // condition to be added
                  dataTestId: `qa-${paymentName}-option`,
                  paymentId: `payment-${optionType}`,
                  paymentInfo: paymentDetails,
                  key: key,
                })
              }
            }
            return <></>
          }
        )
      } else if (enableGlobalPaymentSystem && !isSuccessResponse) {
        return (
          <p>
            {i18nTranslate(
              'payment.gpsFailureMessage',
              'We ran into a technical error, please try again after sometime'
            )}
          </p>
        )
      } else {
        return (
          <p>
            {i18nTranslate(
              'payment.gpsErrorMessage',
              'We ran into a technical error, please try again after sometime'
            )}
          </p>
        )
      }
    }
    return <></>
  }

  /** Get ProcessURL to send handleGPSPaymentTypeSelect for EXPRESS CHECKOUT */
  getProcessorURL = () => {
    let processorURL = ''
    const enableGlobalPaymentSystem = isGPSEnabled()
    const isSuccessResponse = checkoutContainer.isSuccessResponse(
      checkoutContainer.globalPaymentOptionsListResponse
    )

    if (!checkoutContainer.isGlobalPaymentCallInProgress) {
      if (
        enableGlobalPaymentSystem &&
        isSuccessResponse &&
        Object.keys(checkoutContainer.globalPaymentResponse)?.length > 0
      ) {
        return Object.keys(checkoutContainer.globalPaymentResponse)?.map(
          (paymentType, key) => {
            const paymentDetails =
              checkoutContainer.globalPaymentResponse[paymentType] || []
            if (isArray(paymentDetails) && paymentDetails?.length > 0) {
              const paymentName = paymentType?.toLowerCase() || ''
              if (paymentName) {
                processorURL =
                  paymentDetails?.paymentInfo?.[0]?.processorURL || ''
                return processorURL
              }
            }
          }
        )
      }
    }
  }

  renderDescriptionForBankTransferPayment = (paymentType = '') => {
    if (
      paymentType === this.selectedPaymentTypeForAdd &&
      paymentType === 'wire'
    ) {
      return <WirePayment />
    }
    return <></>
  }

  /**
   * @description Renders the section to add a new payment method.
   *
   * Displays a message to add payment methods, then conditionally renders:
   * - The list of options to add a new payment method
   * - The credit card form  and continue button if that option was selected
   * - The continue button for other non-credit card options if one was selected
   */
  renderAddNewPaymentSection = () => {
    const { isFromPDP = false } = this.props
    return (
      <>
        <div
          className={`${isFromPDP ? 'd-grid' : 'add-new-payment-container '}`}>
          <p className="font-weight-bold" tabIndex={0}>
            {i18nTranslate('checkout.addPaymentMethods', 'Add Payment Methods')}
          </p>
          <div className="add-new-payment-option">
            {this.renderAddNewPaymentOptions()}
          </div>
        </div>
        {this.selectedPaymentTypeForAdd === 'creditcard' &&
          this.renderNewCreditCardPaymentForm()}
        {isSepaPaymentType(this.selectedPaymentTypeForAdd) && (
          <SepaIbanForm
            initialIbanValue={this.ibanValueCache}
            onIbanChange={this.handleIbanChange}
            onIbanValidationChange={this.handleIbanValidationChange}
            onIbanEntryActiveChange={this.handleIbanEntryActiveChange}
          />
        )}
        {!isExpressCheckout() &&
          this.selectedPaymentTypeForAdd === 'wire' &&
          this.renderDescriptionForBankTransferPayment(
            this.selectedPaymentTypeForAdd
          )}
        {this.selectedPaymentTypeForAdd !== 'creditcard' &&
          !isSepaPaymentType(this.selectedPaymentTypeForAdd) &&
          this.selectedPaymentTypeForAdd !== '' &&
          this.renderContinueButtonForOtherPayments(
            this.selectedPaymentTypeForAdd
          )}
      </>
    )
  }

  @action
  toggleGPSPaymentAccordion = async () => {
    if (!isExpressCheckout()) {
      return
    }
    this.showPaymentList = !this.showPaymentList
    if (customerContainer.isRegisterUser) {
      this.cvvIframeTokenLoader()
    }
    if (
      !this.showUseDifferentCardForm &&
      !this.showAddNewCardForm &&
      this.showPaymentList
    ) {
      if (checkoutContainer.selectedPaymentType !== 'PAYPAL') {
        paypalContainer.paypalInitiate = false
        paypalContainer.paypalClientCreated = false
      }
      if (checkoutContainer.selectedPaymentType !== 'applepay') {
        applepayContainer.applepayInitiate = false
        applepayContainer.applepayClientCreated = false
      }
      if (checkoutContainer.selectedPaymentType !== 'klarna') {
        checkoutContainer.isToInitiateKlarnaPayment = false
      }
      if (checkoutContainer.selectedPaymentType !== 'alipay') {
        this?.props?.closeAlipayInitiate()
      }
      if (cartContainer.cartResponse?.payments) {
        await this.deleteExistingPayment()
      }
    }
    if (
      !customerContainer.isRegisterUser ||
      !hasNonExcludedSavedPayment(customerContainer.paymentResponse?.payments)
    ) {
      this.handleGPSPaymentTypeSelect(
        APPConfig.getAppConfig()?.defaultPaymentMethod || 'CREDITCARD',
        this.getProcessorURL(),
        0,
        false
      )
    }

    if (
      !hasNonExcludedSavedPayment(customerContainer.paymentResponse?.payments) &&
      (!cartContainer.cartResponse?.payments ||
        cartContainer.cartResponse?.payments?.some(payment =>
          this.customPaymentTypes.includes(payment?.type)
        ))
    ) {
      this.showAddNewCardForm = true
      this.showUseDifferentCardForm = true
      this.showPaymentList =
        !!customerContainer?.paymentResponse.payment?.length
    }
  }

  renderCardDetailsView = (payments = {}) => {
    let card = payments?.accountInfo || {}
    const provider = card?.provider?.toLowerCase() || ''
    const cardNumber = card?.cardNumber || ''
    const endingText = i18nTranslate('payment.cardEndingTxt', '-****')
    if (!cardNumber) {
      return <></>
    }

    return (
      <div
        className="d-flex payment-details-wrapper mt-2"
        data-testid="payment-details-wrapper">
        <PaymentIcon type={provider} />
        <div className="d-flex font-weight-bold">
          <span className="selected-card-info ml-1 d-flex align-items-center font-weight-bold text-capitalize">
            {provider} {endingText} {cardNumber}
          </span>
        </div>
      </div>
    )
  }

  renderSelectedPaymentMethod = () => {
    const paymentsSupported = [
      'creditcard',
      'debitcard',
      'paypal',
      'klarna',
      'alipay',
      'bankTransfer',
      'chile webpay',
      'ChileWebpay',
      'wire',
      'mercado pago',
      'Mercado_Pago',
      'modo',
      'razorpay',
    ]
    return cartContainer?.cartResponse?.payments?.map((payment, index) => {
      let paymentType =
        payment?.name?.toLowerCase() || payment?.type?.toLowerCase() || ''

      // AS per BE requirement,if displayName is coming in response then show otherwise show payment Name
      if (payment?.type.toLowerCase() === 'gps') {
        paymentType = this.gpsDisplayName
          ? this.gpsDisplayName
          : payment?.name.toLowerCase()
      }
      if (paymentsSupported?.includes(paymentType)) {
        if (paymentType === 'creditcard' || paymentType === 'debitcard')
          return this.renderCardDetailsView(payment)
        else {
          return (
            <div
              key={payment + index}
              className="d-flex align-items-center"
              data-testid="payment-details-wrapper">
              <span>{<PaymentIcon type={paymentType} />}</span>
              <span className="ml-1">
                {paymentType?.toUpperCase()?.replace('_', ' ')}
              </span>
              <span className="ml-1">
                {this.renderDescriptionForPayment(paymentType)}
              </span>
            </div>
          )
        }
      }
    })
  }
  /**
   * @description Renders the tabs for Saved Payments and Add New Payment.
   *
   * Sets the default active tab based on saved cards.
   * Renders the Add New Payment section and Saved Payments list conditionally.
   * Handles selecting a new tab.
   * Shows pagination if enabled and multiple pages.
   */
  renderPaymentTabs = () => {
    const defaultActiveKey = this.getDefaultPaymentTab()
    const savedCard = i18nTranslate('checkout.savedPayment', 'Saved Payments')
    const addNewCard = i18nTranslate('checkout.addPayment', 'Add New Payment')
    const { payments = [], pageableInfo = {} } =
      customerContainer.paymentResponse || {}
    const { page = 1, count = 1 } = pageableInfo || {}
    this.currentPage = count == 1 ? page - 1 : page
    this.totalPagesLength = getTotalPagesForPagination(pageableInfo)
    const isPaymentPaginationEnabled =
      APPConfig?.getAppConfig()?.isPaymentPaginationEnabled === 'true' || false
    let isToShowSavedCardTab = this.isToShowSavedCardsTab(payments)
    const isIstallmentEnabled = convertToBoolean(
      APPConfig.getAppConfig()?.enableCheckoutInstallment
    )
    const isSubscriptionItemInCart = cartContainer.hasSubscriptionProduct()
    const hasFilteredPayments = hasNonExcludedSavedPayment(payments)
    const disableExpressCheckoutAccordionView = () => {
      return (
        (!hasFilteredPayments && !cartContainer.cartResponse?.payments) ||
        (!hasFilteredPayments &&
          !cartContainer.cartResponse?.payments?.some(
            payment => !this.customPaymentTypes.includes(payment?.type)
          ))
      )
    }
    const isStoreCreditAvailable =
      cartContainer?.cartResponse?.payments?.filter(
        payment => payment?.type !== 'STORECREDIT'
      )?.length > 0

    if (
      isExpressCheckout() &&
      isStoreCreditAvailable &&
      !this.showPaymentList
    ) {
      return (
        <div
          className="express-payment-list position-relative"
          id="express-payment-list">
          <ExpressSectionHeader
            title={i18nTranslate(
              'expressCheckout.addPaymentMethods',
              'Payment'
            )}
            isOpen={this.showPaymentList}
            disableAccordion={disableExpressCheckoutAccordionView()}
            hideArrows={true}
            onClick={this.toggleGPSPaymentAccordion}
          />
          <div>
            <p className="payment-description">
              {i18nTranslate(
                'expressCheckout.addPaymentMethodsDesc',
                'All transactions are secure and encrypted'
              )}
            </p>
            {this.renderSelectedPaymentMethod()}
          </div>
        </div>
      )
    }

    if (isExpressCheckout()) {
      return (
        <div
          className="express-payment-list position-relative"
          id="express-payment-list">
          <ExpressSectionHeader
            title={i18nTranslate(
              'expressCheckout.addPaymentMethods',
              'Payment'
            )}
            isOpen={this.showPaymentList}
            hideArrows={true}
            disableAccordion={disableExpressCheckoutAccordionView()}
            onClick={() => {
              this.toggleGPSPaymentAccordion()
              this.showUseDifferentCardForm = false
              this.showAddNewCardForm = false
            }}
          />

          {!customerContainer.isRegisterUser && this.gpsPaymentApiLoaded && (
            <>
              <p className="payment-description">
                {i18nTranslate(
                  'expressCheckout.addPaymentMethodsDesc',
                  'All transactions are secure and encrypted'
                )}
              </p>
              {this.renderAddNewPaymentSection()}
            </>
          )}
          {/* hiding the selected payment */}
          {/* {this.renderSelectedPaymentMethod()} */}
          {this.showPaymentList && customerContainer.isRegisterUser && (
            <p
              className={`payment-description ${
                !hasFilteredPayments ? 'mb-0' : 'mb-3'
              }`}>
              {i18nTranslate(
                'expressCheckout.addPaymentMethodsDesc',
                'All transactions are secure and encrypted'
              )}
            </p>
          )}
          {customerContainer.isRegisterUser &&
            hasFilteredPayments &&
            !this.showUseDifferentCardForm && (
              <>{this.renderSavedPaymentsList()}</>
            )}

          {this.showAddNewCardForm &&
            this.showUseDifferentCardForm &&
            customerContainer.isRegisterUser &&
            this.gpsPaymentApiLoaded &&
            this.renderAddNewPaymentSection()}

          {this.showPaymentList &&
            customerContainer.isRegisterUser &&
            hasFilteredPayments && (
              <>
                <h6
                  onClick={() => {
                    this.showAddNewCardForm = true
                    this.showUseDifferentCardForm =
                      !this.showUseDifferentCardForm
                    this.handleGPSPaymentTypeSelect(
                      APPConfig.getAppConfig()?.defaultPaymentMethod ||
                        'CREDITCARD',
                      this.getProcessorURL(),
                      0,
                      true
                    )
                    scrollState.smoothScrollToModalElement(
                      `#qa-checkout-payment-accordion`
                    )
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      this.showAddNewCardForm = true
                      this.showUseDifferentCardForm =
                        !this.showUseDifferentCardForm
                      this.handleGPSPaymentTypeSelect(
                        APPConfig.getAppConfig()?.defaultPaymentMethod ||
                          'CREDITCARD',
                        this.getProcessorURL(),
                        0,
                        true
                      )
                      scrollState.smoothScrollToModalElement(
                        `#qa-checkout-payment-accordion`
                      )
                    }
                  }}
                  className="my-2 d-flex align-items-center payment-tab-express"
                  role="button"
                  tabIndex="0"
                  aria-label={
                    !this.showUseDifferentCardForm
                      ? this.getDifferentCardText()
                      : i18nTranslate(
                          'expressCheckout.expressUseSaveCard',
                          'Use a saved card'
                        )
                  }>
                  {this.getDifferentCardText() && <IoIosAdd />}
                  <span>
                    {!this.showUseDifferentCardForm
                      ? this.getDifferentCardText()
                      : i18nTranslate(
                          'expressCheckout.expressUseSaveCard',
                          'Use a saved card'
                        )}
                  </span>
                </h6>
                {isIstallmentEnabled &&
                  !this.showUseDifferentCardForm &&
                  !isSubscriptionItemInCart && <PaymentInstallment />}
              </>
            )}
        </div>
      )
    }

    return (
      <div className="position-relative mx-0 mx-lg-3 checkout-payment-form">
        {payments?.length !== 0 && (
          <Tabs
            defaultActiveKey={defaultActiveKey}
            id="uncontrolled-tab-example"
            onSelect={tabName => this.handleSelectedPaymentTab(tabName)}
            data-testid="qa-payment-cards-wrapper">
            <Tab
              eventKey="newCard"
              title={addNewCard}
              data-testid="qa-add-payment-tab">
              {this.renderAddNewPaymentSection()}
            </Tab>

            {isToShowSavedCardTab && (
              <Tab
                className="py-4"
                eventKey="savedcards"
                title={savedCard}
                data-testid="qa-saved-payment-tab">
                {this.renderSavedPaymentsList()}
                {this.totalPagesLength > 1 && isPaymentPaginationEnabled && (
                  <Pagination
                    className="payment-pagination-wrapper"
                    handlePagination={this.handlePagination}
                    totalPages={this.totalPagesLength}
                    active={page}
                    numOfPaginationsToShow={this.totalPagesLength}
                    alignPagination={application.isMobile ? 'center' : 'start'}
                  />
                )}
                {isIstallmentEnabled && !isSubscriptionItemInCart && (
                  <PaymentInstallment />
                )}
              </Tab>
            )}
          </Tabs>
        )}

        {payments?.length === 0 && this.gpsPaymentApiLoaded && (
          <Tabs
            defaultActiveKey="newcard"
            id="uncontrolled-tab-example"
            data-testid="qa-payment-cards-wrapper">
            <Tab eventKey="newcard" title={addNewCard}>
              {this.renderAddNewPaymentSection()}
            </Tab>
          </Tabs>
        )}
      </div>
    )
  }

  getDifferentCardText() {
    if (this.gpsPaymentApiLoaded) {
      return i18nTranslate(
        'expressCheckout.expressUseDifferentCard',
        'Use a different card'
      )
    }
  }

  render() {
    const toShowBillingAddressPopup =
      this.props?.isToShowBillingAddressWarningModal ??
      this.isToShowBillingAddressWarningModal
    if (!checkoutContainer.isGlobalPaymentCallInProgress) {
      return (
        <>
          {this.renderPaymentTabs(
            this.showAddNewCardForm,
            this.showUseDifferentCardForm
          )}
          {isExpressCheckout() &&
            this.showModal &&
            this.renderWirePaymentInfo()}
          {this.isToShowEditUpdatePaymentModal && (
            <EditUpdatePaymentModal
              handleCheckoutChange={this.handleDefaultChecked}
              show={this.isToShowEditUpdatePaymentModal}
              closeButton={this.handleUpdateModalClose}
              billingAddressType="checkout"
              IDofCardToBeEdited={this.IdofCardToBeEdited}
              isFromCheckout={true}
              cardDetails={this.cardsDetails}
              closeModalCallback={this.handleUpdateModalClose}
            />
          )}
          {this.isToShowDeleteConfirmationModal && (
            <Modal
              className={`visible confirm-delete-modal-wrapper p-0`}
              show={this.isToShowDeleteConfirmationModal}
              onHide={this.handleDeleteConfirmationModalClose}
              centered
              animation={false}>
              <Modal.Header
                closeButton
                onHide={this.handleDeleteConfirmationModalClose}
                className={'border-bottom-0'}>
                <Modal.Title className="title-Payment-Modal">
                  {i18nTranslate('checkout.deletePayment', 'Delete Payment')}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {this.renderDeletePaymentConfirmationText()}
              </Modal.Body>
            </Modal>
          )}
          <Modal
            show={toShowBillingAddressPopup}
            onHide={() => (this.isToShowBillingAddressWarningModal = false)}
            backdrop="static"
            className="billing-address-warning-modal">
            <Modal.Title className="p-3">
              {i18nTranslate(
                'billingAddressWarning',
                'To use this payment method, you need to update the billing address. Can you proceed with the update?'
              )}
            </Modal.Title>
            <Modal.Body className="pt-0">
              <div className="d-flex justify-content-between">
                <Button
                  data-testid="qa-yes-button"
                  className="w-50 mr-2"
                  variant="primary"
                  type="submit"
                  disabled={this.props.disableButton}
                  onClick={() => {
                    this.isToShowBillingAddressWarningModal = false
                    this.isToShowEditUpdatePaymentModal = true
                    this.cardsDetails = this.selectedPaymentForBillingAddData
                    this.IdofCardToBeEdited =
                      this.selectedPaymentForBillingAddData?.id

                    if (this.isFromDeleteCard) {
                      this.isFromDidMount = true
                      this.isFromDeleteCard = false
                    }
                    customerContainer.paymentResponse?.payments?.find(card => {
                      if (card?.isDefault === true) {
                        card.isDefault = false
                      }
                      if (
                        card?.id === this.selectedPaymentForBillingAddData?.id
                      ) {
                        card.isDefault = true
                        if (
                          !savedCardsContainer.savedCardsList[
                            this.selectedPaymentForBillingAddData?.id
                          ]
                        ) {
                          savedCardsContainer.savedCardsList[
                            this.selectedPaymentForBillingAddData?.id
                          ] = this.createSavedCardCvvContainer()
                        }
                      }
                    })
                    this.selectedPayment = this.selectedPaymentForBillingAddData
                    savedCardsContainer.selectedSavedPaymentId =
                      this.selectedPaymentForBillingAddData?.id || ''
                  }}>
                  {i18nTranslate('yes', 'Yes')}
                </Button>
                <Button
                  data-testid="qa-cancel-button"
                  className="w-50 mr-2"
                  variant="primary"
                  onClick={() => {
                    this.isToShowBillingAddressWarningModal = false
                    this.selectedPayment = {}
                    savedCardsContainer.selectedSavedPaymentId = ''
                  }}>
                  {i18nTranslate('cancel', 'Cancel')}
                </Button>
              </div>
            </Modal.Body>
          </Modal>
        </>
      )
    }

    return <></>
  }
}

export { GlobalPaymentsOptionsList }
export default GlobalPaymentsOptionsList
